import React, { useState, useEffect, useRef } from 'react';
import { 
  ChevronRight, ChevronDown, Search, Upload, RefreshCw, 
  Trash2, Eye, Download, Copy, CheckCircle,
  FolderOpen, X, Folder, Grid, List, File, Plus, Users, MoreVertical, FolderPlus, Filter, RotateCcw,
  FileText, FileSpreadsheet, FileImage, FileVideo, FileAudio, FileArchive, FileCode, Move, AlertCircle, Check, Edit2
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import PDFViewer from '../components/PDFViewer';
import OfficeViewer from '../components/OfficeViewer';

const HospitalRecords = () => {
  const { token } = useAuth();
  
  // Notification state
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });
  
  const [parentFolders, setParentFolders] = useState([]);
  const [selectedPath, setSelectedPath] = useState([]); // Array of path segments
  const [recordFiles, setRecordFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [copiedUrl, setCopiedUrl] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewData, setPreviewData] = useState({ url: '', name: '', type: '' });
  const [expandedFolders, setExpandedFolders] = useState(new Set(['root'])); // Track expanded folders in tree
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [filterMode, setFilterMode] = useState('both'); // 'files', 'folders', 'both'
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [leftPanelWidth, setLeftPanelWidth] = useState(300); // Default width in pixels
  const [isResizing, setIsResizing] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [showAddCustomerModal, setShowAddCustomerModal] = useState(false);
  const [contextMenu, setContextMenu] = useState({ show: false, x: 0, y: 0, file: null });
  const [leftPanelContextMenu, setLeftPanelContextMenu] = useState({ show: false, x: 0, y: 0, folder: null });
  const [showCreateFolderModal, setShowCreateFolderModal] = useState(false);
  const [showCreateTopFolderModal, setShowCreateTopFolderModal] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [showRenameFolderModal, setShowRenameFolderModal] = useState(false);
  const [folderToRename, setFolderToRename] = useState(null);
  const [renameFolderValue, setRenameFolderValue] = useState('');
  const [showRenameFileModal, setShowRenameFileModal] = useState(false);
  const [showRenameTopFolderModal, setShowRenameTopFolderModal] = useState(false);
  const [showMoveTopFolderModal, setShowMoveTopFolderModal] = useState(false);
  const [topFolderToRename, setTopFolderToRename] = useState(null);
  const [topFolderToMove, setTopFolderToMove] = useState(null);
  const [renameTopFolderValue, setRenameTopFolderValue] = useState('');
  const [fileToRename, setFileToRename] = useState(null);
  const [renameFileValue, setRenameFileValue] = useState('');
  const [showRecycleBin, setShowRecycleBin] = useState(false);
  const [recycleBinItems, setRecycleBinItems] = useState([]);
  const [recycleBinBrowseFolder, setRecycleBinBrowseFolder] = useState(null); // For browsing folders
  const [recycleBinBrowsePath, setRecycleBinBrowsePath] = useState([]); // For nested folder navigation in local recycle bin
  const [showRestoreConfirmModal, setShowRestoreConfirmModal] = useState(false);
  const [itemToRestore, setItemToRestore] = useState(null);
  const [showGlobalRecycleBin, setShowGlobalRecycleBin] = useState(false);
  const [globalRecycleBinItems, setGlobalRecycleBinItems] = useState([]);
  const [globalRecycleBinBrowseFolder, setGlobalRecycleBinBrowseFolder] = useState(null); // For browsing global recycle bin
  const [globalRecycleBinBrowseParent, setGlobalRecycleBinBrowseParent] = useState(null); // Parent folder name
  const [globalRecycleBinBrowsePath, setGlobalRecycleBinBrowsePath] = useState([]); // For nested navigation in global
  // Removed expandedVersions state since we're not using version expansion anymore
  const [showVersionsModal, setShowVersionsModal] = useState(false);
  const [selectedFileVersions, setSelectedFileVersions] = useState([]);
  const [selectedFileName, setSelectedFileName] = useState('');
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showDeleteForeverModal, setShowDeleteForeverModal] = useState(false);
  const [itemToDeleteForever, setItemToDeleteForever] = useState(null);
  const [showMoveFileModal, setShowMoveFileModal] = useState(false);
  const [fileToMove, setFileToMove] = useState(null);
  const [moveDestinationPath, setMoveDestinationPath] = useState([]);
  const [moveDestinationKey, setMoveDestinationKey] = useState('');
  const [availableFolders, setAvailableFolders] = useState([]);
  const [customerFormData, setCustomerFormData] = useState({
    first_name: '',
    last_name: '',
    pets: [{ name: '' }],
    phone: '',
    email: '',
    sms_opt_in: true,
    email_subscribed: true
  });
  
  // Folder tree navigation states
  const [expandedFoldersTree, setExpandedFoldersTree] = useState(new Set()); // Track expanded folders in tree view
  const [folderTreeCache, setFolderTreeCache] = useState({}); // Cache subfolders for each path
  const [loadingFolders, setLoadingFolders] = useState(new Set()); // Track which folders are being loaded
  const [refreshingTree, setRefreshingTree] = useState(false); // Track if tree is being refreshed
  
  // Tree context menu states
  const [treeContextMenu, setTreeContextMenu] = useState({ show: false, x: 0, y: 0, folder: null, folderPath: '' });
  const [showCreateSubfolderModal, setShowCreateSubfolderModal] = useState(false);
  const [newSubfolderName, setNewSubfolderName] = useState('');
  const [createSubfolderParent, setCreateSubfolderParent] = useState({ folderPath: '' });
  
  const resizeRef = useRef(null);
  
  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || (window.location.origin.includes("localhost") ? "http://localhost:8000" : window.location.origin + "/pandvah-backend");

  // Fetch parent folders
  const fetchParentFolders = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/records/clients`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setClientFolders(data.clients || []);
      } else {
        console.error('Failed to fetch client folders');
        setClientFolders([]);
      }
    } catch (error) {
      console.error('Error fetching client folders:', error);
      setClientFolders([]);
    } finally {
      setLoading(false);
    }
  };


  // Show notification
  const showNotification = (message, type = 'success') => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: '', type: 'success' });
    }, 3000); // Auto-hide after 3 seconds
  };

  // Fetch files for a specific client
  const fetchClientFiles = async (clientId, subPath = '') => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/records/clients/${clientId}/files`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        // Combine folders and files, folders first
        const folders = data.folders || [];
        const files = data.files || [];
        setRecordFiles([...folders, ...files]);
      } else {
        console.error('Failed to fetch client files');
        setRecordFiles([]);
      }
    } catch (error) {
      console.error('Error fetching client files:', error);
      setRecordFiles([]);
    } finally {
      setLoading(false);
    }
  };

  // Upload file to client folder
  const uploadClientFile = async (clientId, file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${API_BASE_URL}/api/records/clients/${clientId}/files`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (response.ok) {
        await fetchClientFiles(clientId);
        return true;
      } else {
        console.error('Failed to upload client file');
        return false;
      }
    } catch (error) {
      console.error('Error uploading client file:', error);
      return false;
    }
  };

  // Delete file from client folder
  const deleteClientFile = async (fileKey) => {
    try {
      // Extract folder name from file key (format: root_folder/Hospital Records/FolderName/...)
      const pathParts = fileKey.split('/');
      const hospitalRecordsIndex = pathParts.indexOf('Hospital Records');
      if (hospitalRecordsIndex === -1 || hospitalRecordsIndex + 1 >= pathParts.length) {
        console.error('Invalid file key format');
        return false;
      }
      const folderName = pathParts[hospitalRecordsIndex + 1];
      
      const response = await fetch(`${API_BASE_URL}/api/records/hospital/folders/${encodeURIComponent(folderName)}/files/${encodeURIComponent(fileKey)}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        // Refresh current folder view with subpath support
        const rootFolder = selectedPath[0];
        const subPath = selectedPath.slice(1).join('/');
        await fetchHospitalFiles(rootFolder, subPath);
        return true;
      } else {
        const errorData = await response.json();
        console.error('Failed to delete file:', errorData.detail || 'Unknown error');
        showError(errorData.detail || 'Failed to delete file. Please try again.');
        return false;
      }
    } catch (error) {
      console.error('Error deleting file:', error);
      showError('An unexpected error occurred. Please try again.');
      return false;
    }
  };

  // Load parent folders
  const loadParentFolders = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/records/hospital/folders`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setParentFolders(data.folders || []);
      } else {
        console.error('Failed to fetch parent folders');
      }
    } catch (error) {
      console.error('Error fetching parent folders:', error);
    }
  };

  // Fetch files in selected hospital folder
  const fetchHospitalFiles = async (folderName, subPath = '') => {
    setLoading(true);
    try {
      const url = subPath 
        ? `${API_BASE_URL}/api/records/hospital/folders/${encodeURIComponent(folderName)}/files?sub_path=${encodeURIComponent(subPath)}`
        : `${API_BASE_URL}/api/records/hospital/folders/${encodeURIComponent(folderName)}/files`;
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        // Combine folders and files, folders first
        const folders = data.folders || [];
        const files = data.files || [];
        setRecordFiles([...folders, ...files]);
      } else {
        console.error('Failed to fetch hospital files');
        setRecordFiles([]);
      }
    } catch (error) {
      console.error('Error fetching hospital files:', error);
      setRecordFiles([]);
    } finally {
      setLoading(false);
    }
  };

  // Simple folder loading - no letter-based organization needed
  // Load client folders on mount
  useEffect(() => {
    loadParentFolders();
  }, []);

  // Close context menu on outside click
  useEffect(() => {
    const handleClick = () => setContextMenu({ show: false, x: 0, y: 0, file: null });
    if (contextMenu.show) {
      document.addEventListener('click', handleClick);
    }
    return () => document.removeEventListener('click', handleClick);
  }, [contextMenu.show]);

  // Handle panel resizing
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (isResizing) {
        const newWidth = e.clientX;
        if (newWidth >= 200 && newWidth <= 600) { // Min 200px, Max 600px
          setLeftPanelWidth(newWidth);
        }
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing]);

  // Simple folder organization
  const getFilteredFolders = () => {
    return parentFolders.filter(folder =>
      folder.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  // Simple folder selection - no expansion needed

  // Select a folder
  const selectFolder = (folder) => {
    setSelectedPath([folder.name]);
    setRecordFiles([]);
    fetchHospitalFiles(folder.name);
  };

  // Navigate into a subfolder
  const navigateIntoFolder = (folderName) => {
    const newPath = [...selectedPath, folderName];
    setSelectedPath(newPath);
    setRecordFiles([]);
    
    // The first element is always the root folder, rest is subpath
    const rootFolder = newPath[0];
    const subPath = newPath.slice(1).join('/');
    fetchHospitalFiles(rootFolder, subPath);
  };

  // Navigate back to a specific breadcrumb level
  const navigateToBreadcrumb = (index) => {
    const newPath = selectedPath.slice(0, index + 1);
    setSelectedPath(newPath);
    setRecordFiles([]);
    
    const rootFolder = newPath[0];
    const subPath = newPath.slice(1).join('/');
    fetchHospitalFiles(rootFolder, subPath);
  };

  // Get breadcrumb from selected path
  const getBreadcrumb = () => {
    if (selectedPath.length === 0) return 'Select a folder';
    return selectedPath.join(' / ');
  };

  // Fetch subfolders for tree navigation (lazy loading)
  const fetchSubfoldersForTree = async (folderPath) => {
    const cacheKey = folderPath;
    
    console.log('=== TREE FETCH DEBUG ===');
    console.log('fetchSubfoldersForTree called with:', folderPath);
    console.log('Cache key:', cacheKey);
    console.log('Current cache:', folderTreeCache);
    
    // Return cached data if available
    if (folderTreeCache[cacheKey]) {
      console.log('Returning cached data for:', cacheKey, folderTreeCache[cacheKey]);
      return folderTreeCache[cacheKey];
    }
    
    // Mark as loading
    setLoadingFolders(prev => new Set([...prev, cacheKey]));
    
    try {
      const rootFolder = folderPath.split('/')[0];
      const subPath = folderPath.split('/').slice(1).join('/');
      
      console.log('Root folder:', rootFolder);
      console.log('Sub path:', subPath);
      
      const url = subPath 
        ? `${API_BASE_URL}/api/records/hospital/folders/${encodeURIComponent(rootFolder)}/files?sub_path=${encodeURIComponent(subPath)}`
        : `${API_BASE_URL}/api/records/hospital/folders/${encodeURIComponent(rootFolder)}/files`;
      
      console.log('Fetching subfolders from URL:', url);
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      console.log('Response status:', response.status);
      console.log('Response ok:', response.ok);

      if (response.ok) {
        const data = await response.json();
        console.log('Full API response data:', data);
        
        // Hospital Records API returns everything in 'files' array, not separate 'folders'
        // Filter to get only folders
        const allItems = data.files || [];
        const folders = allItems.filter(item => item.type === 'folder');
        
        console.log('All items:', allItems);
        console.log('Extracted folders for', folderPath, ':', folders);
        console.log('Number of folders:', folders.length);
        
        // Cache the subfolders
        setFolderTreeCache(prev => {
          const newCache = {
            ...prev,
            [cacheKey]: folders
          };
          console.log('Setting new cache:', newCache);
          return newCache;
        });
        
        console.log('=== END TREE FETCH DEBUG ===');
        return folders;
      } else {
        console.error('Failed to fetch subfolders, status:', response.status);
        const errorText = await response.text();
        console.error('Error response:', errorText);
      }
    } catch (error) {
      console.error('Error fetching subfolders for tree:', error);
    } finally {
      // Remove loading state
      setLoadingFolders(prev => {
        const newSet = new Set(prev);
        newSet.delete(cacheKey);
        return newSet;
      });
    }
    
    console.log('=== END TREE FETCH DEBUG (no data) ===');
    return [];
  };

  // Toggle folder expansion in tree
  const toggleFolderTreeExpansion = async (folderPath) => {
    const treeKey = folderPath;
    
    const newExpanded = new Set(expandedFoldersTree);
    
    if (newExpanded.has(treeKey)) {
      // Collapse
      newExpanded.delete(treeKey);
      setExpandedFoldersTree(newExpanded);
    } else {
      // Expand - first set expanded state, then fetch
      newExpanded.add(treeKey);
      setExpandedFoldersTree(newExpanded);
      
      // Fetch subfolders (will update cache)
      await fetchSubfoldersForTree(folderPath);
    }
  };

  // Navigate to a folder from tree
  const navigateToFolderFromTree = (folderPath) => {
    const newPath = folderPath.split('/').filter(p => p);
    setSelectedPath(newPath);
    setRecordFiles([]);
    
    const rootFolder = newPath[0];
    if (newPath.length === 1) {
      fetchHospitalFiles(rootFolder);
    } else {
      const subPath = newPath.slice(1).join('/');
      fetchHospitalFiles(rootFolder, subPath);
    }
  };

  // Refresh folder tree - clear all caches and let user re-expand
  const handleRefreshTree = async () => {
    setRefreshingTree(true);
    
    try {
      // Simply clear everything - folders will be re-fetched when user expands them
      setFolderTreeCache({});
      setLoadingFolders(new Set());
      setExpandedFoldersTree(new Set());
      
      // Refresh main view if viewing a folder
      if (selectedPath.length > 0) {
        const rootFolder = selectedPath[0];
        if (selectedPath.length === 1) {
          await fetchHospitalFiles(rootFolder);
        } else {
          const subPath = selectedPath.slice(1).join('/');
          await fetchHospitalFiles(rootFolder, subPath);
        }
      }
    } catch (error) {
      console.error('Error refreshing tree:', error);
    } finally {
      setRefreshingTree(false);
    }
  };


  // Create subfolder in tree
  const handleCreateSubfolder = async () => {
    if (!newSubfolderName.trim()) {
      alert('Please enter a folder name');
      return;
    }
    
    const { folderPath } = createSubfolderParent;
    const rootFolder = folderPath.split('/')[0];
    const subPath = folderPath.split('/').slice(1).join('/');
    
    try {
      const url = subPath
        ? `${API_BASE_URL}/api/records/hospital/folders/${encodeURIComponent(rootFolder)}/create-subfolder?sub_path=${encodeURIComponent(subPath)}`
        : `${API_BASE_URL}/api/records/hospital/folders/${encodeURIComponent(rootFolder)}/create-subfolder`;
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ subfolder_name: newSubfolderName.trim() })
      });

      if (response.ok) {
        // Clear cache for this folder to force refresh
        setFolderTreeCache(prev => {
          const newCache = { ...prev };
          delete newCache[folderPath];
          return newCache;
        });
        
        // Remove from loading state to ensure fresh fetch
        setLoadingFolders(prev => {
          const newSet = new Set(prev);
          newSet.delete(folderPath);
          return newSet;
        });
        
        // Make sure the parent folder is expanded in the tree
        setExpandedFoldersTree(prev => {
          const newExpanded = new Set(prev);
          newExpanded.add(folderPath);
          return newExpanded;
        });
        
        // Force refresh by clearing cache and refetching
        // Use a small delay to ensure state updates are processed
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // Refresh the folder tree by fetching subfolders
        await fetchSubfoldersForTree(folderPath);
        
        // Close modal and reset
        setShowCreateSubfolderModal(false);
        setNewSubfolderName('');
        
        // If currently viewing this folder, refresh main view
        const currentPath = selectedPath.join('/');
        if (currentPath === folderPath || currentPath.startsWith(folderPath + '/')) {
          if (selectedPath.length === 1) {
            await fetchHospitalFiles(rootFolder);
          } else {
            const subPathForFetch = selectedPath.slice(1).join('/');
            await fetchHospitalFiles(rootFolder, subPathForFetch);
          }
        }
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to create folder');
      }
    } catch (error) {
      console.error('Error creating subfolder:', error);
      alert('Failed to create folder');
    }
  };



  // Drag and drop handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (selectedPath.length > 0) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set dragging to false if we're leaving the drop zone completely
    if (e.currentTarget === e.target) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (selectedPath.length === 0) {
      showError('Please select a folder first before uploading files.');
      return;
    }

    const files = Array.from(e.dataTransfer.files);
    if (files.length === 0) return;
    
    const rootFolder = selectedPath[0];
    const subPath = selectedPath.slice(1).join('/');
    
    for (const file of files) {
      await uploadHospitalFile(rootFolder, file, subPath);
    }
  };

  // Upload hospital file
  const uploadHospitalFile = async (folderName, file, subPath = '') => {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const url = subPath 
        ? `${API_BASE_URL}/api/records/hospital/folders/${encodeURIComponent(folderName)}/files?sub_path=${encodeURIComponent(subPath)}`
        : `${API_BASE_URL}/api/records/hospital/folders/${encodeURIComponent(folderName)}/files`;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (response.ok) {
        await fetchHospitalFiles(folderName, subPath);
      } else {
        const errorData = await response.json();
        showError(`Failed to upload ${file.name}: ${errorData.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      showError(`Error uploading ${file.name}: ${error.message}`);
    }
  };

  // Add Customer Modal Functions
  const addPet = () => {
    setCustomerFormData({
      ...customerFormData,
      pets: [...customerFormData.pets, { name: '' }]
    });
  };

  const removePet = (index) => {
    const updatedPets = customerFormData.pets.filter((_, i) => i !== index);
    setCustomerFormData({ ...customerFormData, pets: updatedPets });
  };

  const updatePetName = (index, name) => {
    const updatedPets = [...customerFormData.pets];
    updatedPets[index] = { name };
    setCustomerFormData({ ...customerFormData, pets: updatedPets });
  };

  const resetCustomerForm = () => {
    setCustomerFormData({
      first_name: '',
      last_name: '',
      pets: [{ name: '' }],
      phone: '',
      email: '',
      sms_opt_in: true,
      email_subscribed: true
    });
    setShowAddCustomerModal(false);
  };

  const handleAddCustomer = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/customers`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(customerFormData)
      });

      if (response.ok) {
        resetCustomerForm();
        // Refresh parent folders to show the new customer
        await fetchParentFolders();
      } else {
        const errorData = await response.json();
        console.error('Failed to add customer:', errorData.detail);
      }
    } catch (error) {
      console.error('Error adding customer:', error);
    } finally {
      setLoading(false);
    }
  };

  // Show error modal
  const showError = (message) => {
    setErrorMessage(message);
    setShowErrorModal(true);
  };

  // Create folder (parent or subfolder)
  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;
    
    setLoading(true);
    try {
      if (selectedPath.length === 0) {
        // Creating parent folder in Hospital Records
        const response = await fetch(`${API_BASE_URL}/api/records/hospital/folders`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ folder_name: newFolderName.trim() })
        });

        if (response.ok) {
          setNewFolderName('');
          setShowCreateFolderModal(false);
          // Refresh parent folders list
          await loadParentFolders();
        } else {
          const errorData = await response.json();
          console.error('Failed to create parent folder:', errorData.detail || 'Unknown error');
          showError(errorData.detail || 'Failed to create folder. Please try again.');
        }
      } else {
        // Creating subfolder within selected folder (with subpath support)
        const rootFolder = selectedPath[0];
        const subPath = selectedPath.slice(1).join('/');
        
        const url = subPath 
          ? `${API_BASE_URL}/api/records/hospital/folders/${encodeURIComponent(rootFolder)}/create-subfolder?sub_path=${encodeURIComponent(subPath)}`
          : `${API_BASE_URL}/api/records/hospital/folders/${encodeURIComponent(rootFolder)}/create-subfolder`;
        
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ subfolder_name: newFolderName.trim() })
        });

        if (response.ok) {
          setNewFolderName('');
          setShowCreateFolderModal(false);
          // Refresh current folder view
          await fetchHospitalFiles(rootFolder, subPath);
        } else {
          const errorData = await response.json();
          console.error('Failed to create subfolder:', errorData.detail || 'Unknown error');
          showError(errorData.detail || 'Failed to create subfolder. Please try again.');
        }
      }
    } catch (error) {
      console.error('Error creating folder:', error);
      showError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle create top-level folder (always creates at root)
  const handleCreateTopFolder = async () => {
    if (!newFolderName.trim()) return;
    
    setLoading(true);
    try {
      // Creating parent folder in Hospital Records
      const response = await fetch(`${API_BASE_URL}/api/records/hospital/folders`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ folder_name: newFolderName.trim() })
      });

      if (response.ok) {
        setNewFolderName('');
        setShowCreateTopFolderModal(false);
        // Refresh parent folders list
        await loadParentFolders();
      } else {
        const errorData = await response.json();
        console.error('Failed to create parent folder:', errorData.detail || 'Unknown error');
        showError(errorData.detail || 'Failed to create folder. Please try again.');
      }
    } catch (error) {
      console.error('Error creating folder:', error);
      showError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;
    
    setLoading(true);
    try {
      if (itemToDelete.type === 'folder' || itemToDelete.isFolder) {
        // Delete folder - extract folder key from the full S3 key
        const response = await fetch(`${API_BASE_URL}/api/records/hospital/delete-folder`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ folder_key: itemToDelete.key })
        });
        
        if (response.ok) {
          // Refresh current folder view with subpath support
          const rootFolder = selectedPath[0];
          const subPath = selectedPath.slice(1).join('/');
          await fetchHospitalFiles(rootFolder, subPath);
          
          // Refresh the entire tree
          await handleRefreshTree();
        } else {
          const errorData = await response.json();
          console.error('Failed to delete folder:', errorData.detail || 'Unknown error');
          showError(errorData.detail || 'Failed to delete folder. Please try again.');
        }
      } else {
        // Delete file
        await deleteClientFile(itemToDelete.key);
      }
      
      setShowDeleteConfirmModal(false);
      setItemToDelete(null);
    } catch (error) {
      console.error('Error deleting item:', error);
      showError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle rename folder
  const handleRenameFolder = async () => {
    if (!folderToRename || !renameFolderValue.trim()) return;
    
    setLoading(true);
    try {
      // The rename endpoint expects key WITHOUT prefix
      // Strip prefix if present (tree adds it, but rename doesn't expect it)
      let oldFolderKey = folderToRename.key;
      if (oldFolderKey.includes('Hospital Records/')) {
        oldFolderKey = oldFolderKey.substring(oldFolderKey.indexOf('Hospital Records/'));
      }
      
      const response = await fetch(`${API_BASE_URL}/api/records/hospital/rename-folder`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          old_folder_key: oldFolderKey,
          new_folder_name: renameFolderValue.trim()
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        
        setShowRenameFolderModal(false);
        setFolderToRename(null);
        setRenameFolderValue('');
        
        // Refresh current folder view with subpath support
        const rootFolder = selectedPath[0];
        const subPath = selectedPath.slice(1).join('/');
        await fetchHospitalFiles(rootFolder, subPath);
        
        // Refresh the entire tree
        await handleRefreshTree();
      } else {
        const errorData = await response.json();
        console.error('Failed to rename folder:', errorData.detail || 'Unknown error');
        showError(errorData.detail || 'Failed to rename folder. Please try again.');
      }
    } catch (error) {
      console.error('Error renaming folder:', error);
      showError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle rename file
  const handleRenameFile = async () => {
    if (!fileToRename || !renameFileValue.trim()) return;
    
    setLoading(true);
    try {
      // Get the original filename and extract extension
      const originalName = fileToRename.original_name || fileToRename.name;
      const extension = originalName.includes('.') ? originalName.substring(originalName.lastIndexOf('.')) : '';
      
      // Combine new name with original extension
      const newFilename = renameFileValue.trim() + extension;
      
      const response = await fetch(`${API_BASE_URL}/api/records/hospital/rename-file`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          file_key: fileToRename.key,
          new_filename: newFilename
        })
      });
      
      if (response.ok) {
        setShowRenameFileModal(false);
        setFileToRename(null);
        setRenameFileValue('');
        // Refresh current folder view with subpath support
        const rootFolder = selectedPath[0];
        const subPath = selectedPath.slice(1).join('/');
        await fetchHospitalFiles(rootFolder, subPath);
      } else {
        const errorData = await response.json();
        console.error('Failed to rename file:', errorData.detail || 'Unknown error');
        showError(errorData.detail || 'Failed to rename file. Please try again.');
      }
    } catch (error) {
      console.error('Error renaming file:', error);
      showError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Load available folders for move operation
  const loadAvailableFolders = async (rootFolder, currentPath = []) => {
    try {
      const subPath = currentPath.join('/');
      const url = subPath 
        ? `${API_BASE_URL}/api/records/hospital/folders/${encodeURIComponent(rootFolder)}/files?sub_path=${encodeURIComponent(subPath)}`
        : `${API_BASE_URL}/api/records/hospital/folders/${encodeURIComponent(rootFolder)}/files`;
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        // Filter only folders
        return (data.files || []).filter(item => item.type === 'folder' || item.isFolder);
      }
      return [];
    } catch (error) {
      console.error('Error loading folders:', error);
      return [];
    }
  };

  // Handle move file
  const handleMoveFile = async (draggedFile, destinationFolderKey) => {
    if (!draggedFile) return;
    
    setLoading(true);
    try {
      // Check if it's a folder or file
      const isFolder = draggedFile.type === 'folder';
      const endpoint = isFolder ? 'move-folder' : 'move-file';
      const payload = isFolder ? {
        source_folder_key: draggedFile.key,
        destination_folder_key: destinationFolderKey
      } : {
        file_key: draggedFile.key,
        destination_folder: destinationFolderKey
      };
      
      const response = await fetch(`${API_BASE_URL}/api/records/hospital/${endpoint}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      
      if (response.ok) {
        setShowMoveFileModal(false);
        setFileToMove(null);
        setMoveDestinationPath([]);
        
        // Refresh current folder view
        const rootFolder = selectedPath[0];
        const subPath = selectedPath.slice(1).join('/');
        await fetchHospitalFiles(rootFolder, subPath);
        
        // Refresh the entire tree
        await handleRefreshTree();
      } else {
        const errorData = await response.json();
        console.error(`Failed to move ${isFolder ? 'folder' : 'file'}:`, errorData.detail || 'Unknown error');
        showError(errorData.detail || `Failed to move ${isFolder ? 'folder' : 'file'}. Please try again.`);
      }
    } catch (error) {
      console.error('Error moving:', error);
      showError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle rename top folder
  const handleRenameTopFolder = async () => {
    if (!topFolderToRename || !renameTopFolderValue.trim()) return;
    
    setLoading(true);
    try {
      // Construct the old folder key - use path if available, otherwise construct it
      const oldFolderKey = topFolderToRename.path || topFolderToRename.key || `Hospital Records/${topFolderToRename.name}/`;
      
      const response = await fetch(`${API_BASE_URL}/api/records/hospital/rename-folder`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          old_folder_key: oldFolderKey,
          new_folder_name: renameTopFolderValue.trim()
        })
      });
      
      if (response.ok) {
        setShowRenameTopFolderModal(false);
        setTopFolderToRename(null);
        setRenameTopFolderValue('');
        // Refresh parent folders list
        await loadParentFolders();
        // Clear selection if renamed folder was selected
        if (selectedPath[0] === topFolderToRename.name) {
          setSelectedPath([]);
          setRecordFiles([]);
        }
      } else {
        const errorData = await response.json();
        console.error('Failed to rename folder:', errorData.detail || 'Unknown error');
        showError(errorData.detail || 'Failed to rename folder. Please try again.');
      }
    } catch (error) {
      console.error('Error renaming folder:', error);
      showError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle delete top folder
  const handleDeleteTopFolder = async (folder) => {
    if (!window.confirm(`Are you sure you want to delete "${folder.name}" and all its contents?`)) {
      return;
    }
    
    setLoading(true);
    try {
      const folderKey = folder.path || folder.key || `Hospital Records/${folder.name}/`;
      
      const response = await fetch(`${API_BASE_URL}/api/records/hospital/delete-folder`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ folder_key: folderKey })
      });
      
      if (response.ok) {
        // Refresh parent folders list
        await loadParentFolders();
        // Clear selection if deleted folder was selected
        if (selectedPath[0] === folder.name) {
          setSelectedPath([]);
          setRecordFiles([]);
        }
      } else {
        const errorData = await response.json();
        console.error('Failed to delete folder:', errorData.detail || 'Unknown error');
        showError(errorData.detail || 'Failed to delete folder. Please try again.');
      }
    } catch (error) {
      console.error('Error deleting folder:', error);
      showError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle move top folder
  const handleMoveTopFolder = async (destinationFolderKey) => {
    if (!topFolderToMove) return;
    
    setLoading(true);
    try {
      const sourceFolderKey = topFolderToMove.path || topFolderToMove.key || `Hospital Records/${topFolderToMove.name}/`;
      
      // In this case, moving means making it a subfolder of another top folder
      const response = await fetch(`${API_BASE_URL}/api/records/hospital/move-folder`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          source_folder_key: sourceFolderKey,
          destination_folder_key: destinationFolderKey
        })
      });
      
      if (response.ok) {
        setShowMoveTopFolderModal(false);
        setTopFolderToMove(null);
        setMoveDestinationPath([]);
        setMoveDestinationKey('');
        // Refresh parent folders list
        await loadParentFolders();
        // Clear selection if moved folder was selected
        if (selectedPath[0] === topFolderToMove.name) {
          setSelectedPath([]);
          setRecordFiles([]);
        }
      } else {
        const errorData = await response.json();
        console.error('Failed to move folder:', errorData.detail || 'Unknown error');
        showError(errorData.detail || 'Failed to move folder. Please try again.');
      }
    } catch (error) {
      console.error('Error moving folder:', error);
      showError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Fetch recycle bin items
  const fetchRecycleBin = async (folderName) => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/records/hospital/folders/${encodeURIComponent(folderName)}/recycle-bin`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setRecycleBinItems(data.items || []);
      } else {
        console.error('Failed to fetch recycle bin');
        setRecycleBinItems([]);
      }
    } catch (error) {
      console.error('Error fetching recycle bin:', error);
      setRecycleBinItems([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch global recycle bin (all hospital folders)
  const fetchGlobalRecycleBin = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/records/hospital/recycle-bin/all`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setGlobalRecycleBinItems(data.items || []);
      } else {
        console.error('Failed to fetch global recycle bin');
        setGlobalRecycleBinItems([]);
      }
    } catch (error) {
      console.error('Error fetching global recycle bin:', error);
      setGlobalRecycleBinItems([]);
    } finally {
      setLoading(false);
    }
  };

  // Browse into a deleted folder in recycle bin
  const browseDeletedFolder = async (folderName, deletedFolderName) => {
    setLoading(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/records/hospital/folders/${encodeURIComponent(folderName)}/recycle-bin/folder/${encodeURIComponent(deletedFolderName)}/browse`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setRecycleBinItems(data.files || []);
        setRecycleBinBrowseFolder(deletedFolderName);
      } else {
        console.error('Failed to browse deleted folder');
        showNotification('Failed to browse folder', 'error');
      }
    } catch (error) {
      console.error('Error browsing deleted folder:', error);
      showNotification('Error browsing folder', 'error');
    } finally {
      setLoading(false);
    }
  };


  // Browse into a deleted folder in global recycle bin
  const browseGlobalDeletedFolder = async (parentFolderName, deletedFolderName, subPath = '') => {
    setLoading(true);
    try {
      const subPathParam = subPath ? `&sub_path=${encodeURIComponent(subPath)}` : '';
      const response = await fetch(
        `${API_BASE_URL}/api/records/hospital/recycle-bin/folder/${encodeURIComponent(deletedFolderName)}/browse?parent_folder=${encodeURIComponent(parentFolderName)}${subPathParam}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        // Combine files and folders
        const allItems = [...(data.folders || []), ...(data.files || [])];
        setGlobalRecycleBinItems(allItems);
        setGlobalRecycleBinBrowseFolder(deletedFolderName);
        setGlobalRecycleBinBrowseParent(parentFolderName);
      } else {
        console.error('Failed to browse deleted folder');
        showNotification('Failed to browse folder', 'error');
      }
    } catch (error) {
      console.error('Error browsing deleted folder:', error);
      showNotification('Error browsing folder', 'error');
    } finally {
      setLoading(false);
    }
  };



  // Fetch file versions
  const fetchFileVersions = async (clientId, fileKey, fileName) => {
    setLoading(true);
    try {
      const encodedKey = encodeURIComponent(fileKey);
      const response = await fetch(`${API_BASE_URL}/api/records/clients/${clientId}/files/${encodedKey}/versions`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSelectedFileVersions(data.versions || []);
        setSelectedFileName(data.original_filename || fileName);
        setShowVersionsModal(true);
      } else {
        console.error('Failed to fetch file versions');
      }
    } catch (error) {
      console.error('Error fetching file versions:', error);
    } finally {
      setLoading(false);
    }
  };

  // Restore from recycle bin - show confirmation first
  const handleRestoreItem = async (item) => {
    setItemToRestore(item);
    setShowRestoreConfirmModal(true);
  };

  // Restore from global recycle bin - show confirmation first  
  const handleRestoreGlobalItem = async (item) => {
    setItemToRestore(item);
    setShowRestoreConfirmModal(true);
  };

  // Confirm and execute restore
  const confirmRestore = async () => {
    const item = itemToRestore;
    if (!item) return;

    setLoading(true);
    setShowRestoreConfirmModal(false);

    try {
      // For folders (subfolders in browse view), they might not have a real file key
      // Skip the key check for folders - backend will handle them
      if (!item.key && item.type !== 'folder') {
        showNotification('Cannot restore item: Invalid item key', 'error');
        setLoading(false);
        setItemToRestore(null);
        return;
      }
      
      const response = await fetch(`${API_BASE_URL}/api/records/hospital/recycle-bin/restore`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ item_key: item.key })
      });

      if (response.ok) {
        // Successfully restored - no toast, just refresh
        
        // Determine which folder to refresh
        let folderToRefresh = null;
        
        // Check if this is from global recycle bin (has folder_name property or browsing)
        if (globalRecycleBinBrowseFolder) {
          // Browsing global recycle bin - use the parent folder
          folderToRefresh = globalRecycleBinBrowseParent;
          
          // Clear browse state and go back to main view
          setGlobalRecycleBinBrowseFolder(null);
          setGlobalRecycleBinBrowseParent(null);
          setGlobalRecycleBinBrowsePath([]);
          await fetchGlobalRecycleBin();
        } else if (item.folder_name) {
          // Global restore from main view
          folderToRefresh = item.folder_name;
          await fetchGlobalRecycleBin();
        } else {
          // Local folder restore
          if (recycleBinBrowseFolder) {
            setRecycleBinBrowseFolder(null);
            setRecycleBinBrowsePath([]);
          }
          const folderName = selectedPath[0];
          await fetchRecycleBin(folderName);
          const rootFolder = selectedPath[0];
          const subPath = selectedPath.slice(1).join('/');
          await fetchHospitalFiles(rootFolder, subPath);
          
          setLoading(false);
          setItemToRestore(null);
          return;
        }
        
        // Auto-refresh main hospital folder view if currently viewing the restored folder
        if (folderToRefresh && selectedPath.length > 0 && selectedPath[0] === folderToRefresh) {
          const rootFolder = selectedPath[0];
          const subPath = selectedPath.slice(1).join('/');
          await fetchHospitalFiles(rootFolder, subPath);
        }
      } else {
        const errorData = await response.json();
        showNotification(`Failed to restore: ${errorData.detail || 'Unknown error'}`, 'error');
      }
    } catch (error) {
      console.error('Error restoring item:', error);
      showNotification('An error occurred while restoring. Please try again.', 'error');
    } finally {
      setLoading(false);
      setItemToRestore(null);
    }
  };

  // Permanently delete from recycle bin
  const handlePermanentDelete = async (item) => {
    if (!selectedPath[0]) return;
    
    // Show custom modal instead of window.confirm
    setItemToDeleteForever(item);
    setShowDeleteForeverModal(true);
  };

  // Confirm permanent delete
  const confirmPermanentDelete = async () => {
    if (!itemToDeleteForever) return;
    
    setLoading(true);
    try {
      // Use hospital records endpoint
      const folderName = selectedPath[0];
      
      const response = await fetch(`${API_BASE_URL}/api/records/hospital/recycle-bin/permanent-delete`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          item_key: itemToDeleteForever.key,
          folder_name: folderName
        })
      });

      if (response.ok) {
        setShowDeleteForeverModal(false);
        setItemToDeleteForever(null);
        await fetchRecycleBin(folderName);
      } else {
        const errorData = await response.json();
        console.error('Failed to permanently delete item:', errorData.detail || 'Unknown error');
        showError(errorData.detail || 'Failed to permanently delete item.');
      }
    } catch (error) {
      console.error('Error permanently deleting item:', error);
      showError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Permanently delete from global recycle bin
  const handlePermanentDeleteGlobal = async (item) => {
    // Show custom modal
    setItemToDeleteForever({ ...item, isGlobal: true });
    setShowDeleteForeverModal(true);
  };

  // Confirm permanent delete from global recycle bin
  const confirmPermanentDeleteGlobal = async () => {
    if (!itemToDeleteForever || !itemToDeleteForever.isGlobal) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/records/hospital/recycle-bin/permanent-delete`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ item_key: itemToDeleteForever.key })
      });

      if (response.ok) {
        setShowDeleteForeverModal(false);
        setItemToDeleteForever(null);
        await fetchGlobalRecycleBin();
      } else {
        const errorData = await response.json();
        console.error('Failed to permanently delete item:', errorData.detail || 'Unknown error');
        showError(errorData.detail || 'Failed to permanently delete item.');
      }
    } catch (error) {
      console.error('Error permanently deleting item:', error);
      showError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Helper functions
  const isPreviewable = (fileName) => {
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.svg'];
    const pdfExtensions = ['.pdf'];
    const officeExtensions = ['.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx'];
    const extension = fileName.toLowerCase().substring(fileName.lastIndexOf('.'));
    return imageExtensions.includes(extension) || pdfExtensions.includes(extension) || officeExtensions.includes(extension);
  };

  const isImageFile = (fileName) => {
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.svg'];
    const extension = fileName.toLowerCase().substring(fileName.lastIndexOf('.'));
    return imageExtensions.includes(extension);
  };

  const getFileType = (fileName) => {
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.svg'];
    const officeExtensions = ['.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx'];
    const extension = fileName.toLowerCase().substring(fileName.lastIndexOf('.'));
    if (imageExtensions.includes(extension)) return 'image';
    if (['.pdf'].includes(extension)) return 'pdf';
    if (officeExtensions.includes(extension)) return 'office';
    return 'unknown';
  };

  // Get files grouped by original filename for version count
  const getFilesWithVersionCount = () => {
    const files = filteredRecords.filter(r => r.type === 'file');
    const folders = filteredRecords.filter(r => r.type === 'folder');
    
    // Group files by original_name to count versions
    const fileVersionCounts = {};
    files.forEach(file => {
      const originalName = file.original_name || file.name;
      if (!fileVersionCounts[originalName]) {
        fileVersionCounts[originalName] = [];
      }
      fileVersionCounts[originalName].push(file);
    });
    
    // Get unique files (latest version of each)
    const uniqueFiles = [];
    const processedNames = new Set();
    
    files.forEach(file => {
      const originalName = file.original_name || file.name;
      if (!processedNames.has(originalName)) {
        const versions = fileVersionCounts[originalName];
        // Sort by version (descending) to get latest
        versions.sort((a, b) => (b.version || 0) - (a.version || 0));
        const latestVersion = versions[0];
        
        uniqueFiles.push({
          ...latestVersion,
          versionCount: versions.length,
          hasMultipleVersions: versions.length > 1
        });
        processedNames.add(originalName);
      }
    });
    
    return [...folders, ...uniqueFiles];
  };

  const getFileIcon = (filename, size = 'default') => {
    const ext = filename.split('.').pop().toLowerCase();
    const sizeClass = size === 'large' ? 'h-12 w-12' : 'h-8 w-8';
    
    // Return icon with extension badge
    const renderIconWithExtension = (Icon, color) => {
      if (size === 'large') {
        return (
          <div className="relative">
            <Icon className={`${sizeClass} ${color}`} />
            <div className={`absolute -bottom-1 -right-1 bg-white border border-gray-300 rounded px-1 text-[10px] font-bold ${color.replace('text-', 'text-')}`}>
              .{ext}
            </div>
          </div>
        );
      } else {
        return (
          <div className="flex flex-col items-center gap-0.5">
            <Icon className={`${sizeClass} ${color}`} />
            <span className={`text-[9px] font-semibold ${color}`}>.{ext}</span>
          </div>
        );
      }
    };
    
    // Image files
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp', 'ico'].includes(ext)) {
      return renderIconWithExtension(FileImage, 'text-green-500');
    } 
    // PDF files
    else if (ext === 'pdf') {
      return renderIconWithExtension(FileText, 'text-red-500');
    } 
    // Word documents
    else if (['doc', 'docx'].includes(ext)) {
      return renderIconWithExtension(FileText, 'text-theme-blue');
    } 
    // Excel spreadsheets
    else if (['xls', 'xlsx', 'csv'].includes(ext)) {
      return renderIconWithExtension(FileSpreadsheet, 'text-green-600');
    } 
    // PowerPoint presentations
    else if (['ppt', 'pptx'].includes(ext)) {
      return renderIconWithExtension(FileText, 'text-orange-600');
    } 
    // Video files
    else if (['mp4', 'avi', 'mov', 'wmv', 'flv', 'mkv', 'webm'].includes(ext)) {
      return renderIconWithExtension(FileVideo, 'text-purple-500');
    } 
    // Audio files
    else if (['mp3', 'wav', 'aac', 'flac', 'ogg', 'm4a'].includes(ext)) {
      return renderIconWithExtension(FileAudio, 'text-pink-500');
    } 
    // Archive files
    else if (['zip', 'rar', '7z', 'tar', 'gz'].includes(ext)) {
      return renderIconWithExtension(FileArchive, 'text-yellow-600');
    } 
    // Code files
    else if (['js', 'jsx', 'ts', 'tsx', 'py', 'java', 'cpp', 'c', 'html', 'css', 'json', 'xml'].includes(ext)) {
      return renderIconWithExtension(FileCode, 'text-gray-600');
    } 
    // Text files
    else if (['txt', 'md', 'log'].includes(ext)) {
      return renderIconWithExtension(FileText, 'text-gray-500');
    } 
    // Default file icon
    else {
      return renderIconWithExtension(File, 'text-gray-400');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const copyUrlToClipboard = (url) => {
    navigator.clipboard.writeText(url).then(() => {
      setCopiedUrl(true);
      setTimeout(() => setCopiedUrl(false), 2000);
    }).catch(err => {
      console.error('Failed to copy URL:', err);
    });
  };

  const openPreview = (file) => {
    const fileType = getFileType(file.name);
    let fileUrl = file.url;
    
    // For PDF and Office files, use our backend proxy to avoid CORS issues
    if ((fileType === 'pdf' || fileType === 'office') && file.key) {
      fileUrl = `${API_BASE_URL}/api/records/proxy-pdf?s3_key=${encodeURIComponent(file.key)}`;
    }
    
    setPreviewData({
      url: fileUrl,
      name: file.name,
      type: fileType,
      s3Key: file.key // Store S3 key for potential use
    });
    setShowPreviewModal(true);
  };

  const filteredFolders = getFilteredFolders();

  // Simple search functionality - no auto-expand needed

  // Filter files/folders based on selected filter mode
  const getFilteredRecords = () => {
    if (filterMode === 'files') {
      return recordFiles.filter(item => item.type === 'file');
    } else if (filterMode === 'folders') {
      return recordFiles.filter(item => item.type === 'folder');
    }
    return recordFiles; // 'both' - show all
  };

  const filteredRecords = getFilteredRecords();


  // Recursive Folder Tree Item Component for Hospital Records
  const FolderTreeItem = ({ folderName, folderPath, level = 0 }) => {
    const treeKey = folderPath;
    const isExpanded = expandedFoldersTree.has(treeKey);
    const isLoading = loadingFolders.has(folderPath);
    const subfolders = folderTreeCache[folderPath] || [];
    
    // Check if this folder is the exact selected folder (not parent)
    const currentPath = selectedPath.join('/');
    const isSelected = currentPath === folderPath;
    
    const hasSubfolders = subfolders.length > 0 || !folderTreeCache[folderPath];
    
    return (
      <div className="select-none">
        <div
          className={`flex items-center px-2 py-1.5 rounded cursor-pointer group ${
            isSelected 
              ? 'bg-blue-100 text-blue-700' 
              : 'hover:bg-gray-100 text-gray-700'
          }`}
          style={{ paddingLeft: `${level * 16 + 8}px` }}
          onContextMenu={(e) => {
            e.preventDefault();
            e.stopPropagation();
            
            // Construct the full S3 key for this folder WITH bucket prefix
            // Hospital delete endpoint expects the full key including prefix
            const folderKey = `ah/pvah1000/Hospital Records/${folderPath}/`;
            
            setTreeContextMenu({
              show: true,
              x: e.clientX,
              y: e.clientY,
              folder: { 
                name: folderName,
                key: folderKey,
                type: 'folder'
              },
              folderPath: folderPath
            });
          }}
        >
          {/* Chevron icon for expand/collapse */}
          {hasSubfolders && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                toggleFolderTreeExpansion(folderPath);
              }}
              className="mr-1 p-0.5 hover:bg-gray-200 rounded"
            >
              {isLoading ? (
                <RefreshCw className="h-3.5 w-3.5 text-gray-400 animate-spin" />
              ) : isExpanded ? (
                <ChevronDown className="h-3.5 w-3.5 text-gray-500" />
              ) : (
                <ChevronRight className="h-3.5 w-3.5 text-gray-500" />
              )}
            </button>
          )}
          
          {/* Folder icon and name */}
          <div
            onClick={() => navigateToFolderFromTree(folderPath)}
            className="flex items-center flex-1 min-w-0"
          >
            <Folder className={`h-3.5 w-3.5 mr-1.5 flex-shrink-0 ${
              isSelected ? 'text-theme-blue' : 'text-gray-500'
            }`} />
            <span className="text-sm truncate">{folderName}</span>
          </div>
        </div>
        
        {/* Render subfolders if expanded */}
        {isExpanded && subfolders.length > 0 && (
          <div>
            {subfolders.map((subfolder) => {
              const subfolderPath = `${folderPath}/${subfolder.name}`;
              return (
                <FolderTreeItem
                  key={subfolder.name}
                  folderName={subfolder.name}
                  folderPath={subfolderPath}
                  level={level + 1}
                />
              );
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Success Toast */}
      {copiedUrl && (
        <div className="fixed top-4 right-4 z-50 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg flex items-center space-x-2">
          <CheckCircle className="h-5 w-5" />
          <span className="font-medium">URL copied to clipboard!</span>
        </div>
      )}

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="bg-white rounded-xl shadow-lg overflow-visible flex flex-col" style={{ height: 'calc(100vh - 120px)' }}>
          {/* Header - Inside the main card, matching Business Info style */}
          <div className="px-6 py-4 bg-gray-50 border-b border-gray-200 relative z-10 flex-shrink-0">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">
                Hospital Records
              </h2>
              <button
                onClick={() => {
                  setShowGlobalRecycleBin(true);
                  fetchGlobalRecycleBin();
                }}
                className="px-2.5 py-1.5 text-xs text-gray-600 border border-gray-300 rounded hover:bg-gray-50 transition-colors flex items-center space-x-1.5"
                title="View All Deleted Items"
              >
                <Trash2 className="h-3.5 w-3.5" />
                <span>Recycle Bin</span>
              </button>
            </div>
          </div>

          {/* Breadcrumb Path - Sticky below top nav */}
          <div className="sticky z-40 bg-gray-100 border-b border-gray-300 px-6 py-3 shadow-sm flex-shrink-0 flex items-center justify-between" style={{ top: '103px' }}>
            <div className="flex items-center text-sm text-gray-700 font-medium">
              <Folder className="h-4 w-4 mr-2 text-gray-500" />
              {selectedPath.length === 0 ? (
                'Select a folder'
              ) : (
                <div className="flex items-center">
                  {selectedPath.map((segment, index) => (
                    <React.Fragment key={index}>
                      <button
                        onClick={() => navigateToBreadcrumb(index)}
                        className="hover:text-theme-blue hover:underline"
                      >
                        {segment}
                      </button>
                      {index < selectedPath.length - 1 && (
                        <ChevronRight className="h-4 w-4 mx-1 text-gray-400" />
                      )}
                    </React.Fragment>
                  ))}
                </div>
              )}
            </div>
            <button
              onClick={() => setShowCreateTopFolderModal(true)}
              className="px-3 py-1.5 text-xs bg-green-600 text-white rounded hover:bg-green-700 transition-colors flex items-center space-x-1.5"
            >
              <FolderPlus className="h-3.5 w-3.5" />
              <span>Create Top Folder</span>
            </button>
          </div>

          {/* Search and Toolbar Row - Sticky below breadcrumb */}
          <div className="sticky z-40 flex bg-white border-b border-gray-200 flex-shrink-0" style={{ top: '151px' }}>
            {/* Search Records - Left side */}
            <div className="relative" style={{ width: `${leftPanelWidth}px` }}>
              <Search className="absolute left-6 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 z-10" />
              <input
                type="text"
                placeholder="Search Records"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-14 pr-6 py-3.5 text-sm bg-white border-0 border-r border-gray-200 focus:outline-none focus:ring-0"
              />
            </div>

            {/* Resizer Handle - Visual separator */}
            <div className="w-1 bg-gray-300" />

            {/* Toolbar - Right side */}
            <div className="flex-1 px-6 flex items-center justify-between" style={{ paddingTop: '0.625rem', paddingBottom: '0.48rem' }}>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => {
                    if (selectedPath.length > 0) {
                      const clientId = parentFolders.find(c => c.name === selectedPath[0])?.id;
                      if (clientId) fetchClientFiles(clientId);
                    }
                  }}
                  disabled={loading || selectedPath.length === 0}
                  className="px-2.5 py-1.5 text-xs text-gray-600 border border-gray-300 rounded hover:bg-gray-50 transition-colors flex items-center space-x-1.5 disabled:opacity-50"
                >
                  <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
                  <span>Refresh</span>
                </button>
                
                {selectedPath.length > 0 && (
                  <label className="px-2.5 py-1.5 text-xs bg-theme-blue text-white rounded hover:bg-blue-700 transition-colors cursor-pointer flex items-center space-x-1.5">
                    <Upload className="h-3.5 w-3.5" />
                    <span>Upload Files</span>
                    <input
                      type="file"
                      multiple
                      onChange={async (e) => {
                        const files = Array.from(e.target.files);
                        const rootFolder = selectedPath[0];
                        const subPath = selectedPath.slice(1).join('/');
                        if (rootFolder) {
                          for (const file of files) {
                            await uploadHospitalFile(rootFolder, file, subPath);
                          }
                        }
                        e.target.value = '';
                      }}
                      className="hidden"
                    />
                  </label>
                )}

                {selectedPath.length > 0 && (
                  <button
                    onClick={() => setShowCreateFolderModal(true)}
                    className="px-2.5 py-1.5 text-xs text-green-600 border border-green-300 rounded hover:bg-green-50 transition-colors flex items-center space-x-1.5"
                  >
                    <FolderPlus className="h-3.5 w-3.5" />
                    <span>New Folder</span>
                  </button>
                )}
              </div>
              
              {/* View Mode Toggle */}
              <div className="flex items-center space-x-2">
                {/* Filter Dropdown */}
                <div className="relative">
                  <button
                    onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                    className="p-1.5 border border-gray-300 rounded text-gray-600 hover:bg-gray-100 transition-colors"
                    title="Filter"
                  >
                    <Filter className="h-3.5 w-3.5" />
                  </button>
                  
                  {showFilterDropdown && (
                    <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-[70]">
                      <div className="py-1">
                        <button
                          onClick={() => {
                            setFilterMode('both');
                            setShowFilterDropdown(false);
                          }}
                          className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 flex items-center justify-between ${
                            filterMode === 'both' ? 'bg-blue-50 text-theme-blue' : 'text-gray-700'
                          }`}
                        >
                          <span>Show Both</span>
                          {filterMode === 'both' && <CheckCircle className="h-4 w-4" />}
                        </button>
                        <button
                          onClick={() => {
                            setFilterMode('files');
                            setShowFilterDropdown(false);
                          }}
                          className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 flex items-center justify-between ${
                            filterMode === 'files' ? 'bg-blue-50 text-theme-blue' : 'text-gray-700'
                          }`}
                        >
                          <span>Show Files Only</span>
                          {filterMode === 'files' && <CheckCircle className="h-4 w-4" />}
                        </button>
                        <button
                          onClick={() => {
                            setFilterMode('folders');
                            setShowFilterDropdown(false);
                          }}
                          className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 flex items-center justify-between ${
                            filterMode === 'folders' ? 'bg-blue-50 text-theme-blue' : 'text-gray-700'
                          }`}
                        >
                          <span>Show Folders Only</span>
                          {filterMode === 'folders' && <CheckCircle className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* View Mode Toggle */}
                <div className="flex border border-gray-300 rounded">
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`p-1.5 ${viewMode === 'grid' ? 'bg-blue-100 text-theme-blue' : 'text-gray-600 hover:bg-gray-100'}`}
                  >
                    <Grid className="h-3.5 w-3.5" />
                  </button>
                  <button
                    onClick={() => setViewMode('list')}
                    className={`p-1.5 ${viewMode === 'list' ? 'bg-blue-100 text-theme-blue' : 'text-gray-600 hover:bg-gray-100'}`}
                  >
                    <List className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content - Two Panel Layout with independent scrolling */}
          <div className="flex flex-1 overflow-hidden">
            {/* Left Navigation Panel - Independently Scrollable */}
            <div 
              className="bg-white border-r border-gray-300 flex flex-col flex-shrink-0"
              style={{ width: `${leftPanelWidth}px` }}
            >
            {/* Folder List */}
            <div className="p-2 flex-1 overflow-y-auto">
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw className="h-6 w-6 animate-spin text-gray-400" />
                </div>
              ) : (
                <>
                  {filteredFolders.length === 0 ? (
                    <div className="text-center py-8">
                      <Folder className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                      <p className="text-gray-500 text-sm">No folders found</p>
                    </div>
                  ) : (
                    filteredFolders.map((folder) => {
                      const isSelected = selectedPath[0] === folder.name && selectedPath.length === 1;
                      const isExpanded = expandedFoldersTree.has(folder.name);
                      const subfolders = folderTreeCache[folder.name] || [];
                      const isLoadingTree = loadingFolders.has(folder.name);
                      
                      return (
                        <div key={folder.id} className="mb-1">
                          <div
                            className={`flex items-center px-2 py-2 rounded cursor-pointer ${
                              isSelected 
                                ? 'bg-blue-100 text-blue-700' 
                                : 'hover:bg-gray-100 text-gray-700'
                            }`}
                          >
                            {/* Expand/Collapse chevron */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleFolderTreeExpansion(folder.name);
                              }}
                              className="mr-1 p-0.5 hover:bg-gray-200 rounded"
                            >
                              {isLoadingTree ? (
                                <RefreshCw className="h-3.5 w-3.5 text-gray-400 animate-spin" />
                              ) : isExpanded ? (
                                <ChevronDown className="h-3.5 w-3.5 text-gray-500" />
                              ) : (
                                <ChevronRight className="h-3.5 w-3.5 text-gray-500" />
                              )}
                            </button>
                            
                            {/* Folder click area */}
                            <div
                              onClick={() => selectFolder(folder)}
                              onContextMenu={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setLeftPanelContextMenu({
                                  show: true,
                                  x: e.clientX,
                                  y: e.clientY,
                                  folder: folder
                                });
                              }}
                              className="flex items-center flex-1 min-w-0"
                            >
                              <FolderOpen className={`h-4 w-4 mr-2 ${isSelected ? 'text-theme-blue' : 'text-gray-500'}`} />
                              <span className="text-sm truncate">{folder.name}</span>
                            </div>
                          </div>
                          
                          {/* Subfolder tree */}
                          {isExpanded && (
                            <div className="ml-2 border-l border-gray-200 pl-1">
                              {isLoadingTree ? (
                                <div className="px-2 py-2 text-xs text-gray-400">
                                  Loading...
                                </div>
                              ) : subfolders.length > 0 ? (
                                subfolders.map((subfolder) => {
                                  const subfolderPath = `${folder.name}/${subfolder.name}`;
                                  return (
                                    <FolderTreeItem
                                      key={subfolder.name}
                                      folderName={subfolder.name}
                                      folderPath={subfolderPath}
                                      level={1}
                                    />
                                  );
                                })
                              ) : (
                                <div className="px-2 py-2 text-xs text-gray-400">
                                  No subfolders
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
                </>
              )}
            </div>
          </div>

          {/* Resizer Handle */}
          <div
            ref={resizeRef}
            onMouseDown={() => setIsResizing(true)}
            className={`w-1 bg-gray-300 hover:bg-theme-blue cursor-col-resize transition-colors ${
              isResizing ? 'bg-theme-blue' : ''
            }`}
          />

          {/* Right Content Panel - Independently Scrollable */}
          <div className="flex-1 flex flex-col overflow-hidden">
            {/* Files Display Area - Scrollable */}
            <div 
              className="flex-1 overflow-y-auto bg-gray-50 p-6 relative"
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
            >
              {/* Drag and Drop Overlay */}
              {isDragging && selectedPath.length > 0 && (
                <div className="absolute inset-0 bg-theme-blue bg-opacity-20 border-4 border-dashed border-theme-blue flex items-center justify-center z-50">
                  <div className="bg-white rounded-lg p-6 shadow-xl">
                    <Upload className="h-12 w-12 text-theme-blue mx-auto mb-3" />
                    <p className="text-lg font-semibold text-gray-900">Drop files here</p>
                    <p className="text-sm text-gray-600 mt-1">to upload to {selectedPath[0]}</p>
                  </div>
                </div>
              )}

              {selectedPath.length === 0 ? (
                <div className="text-center py-12">
                  <FolderOpen className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600 text-lg">Select a folder</p>
                  <p className="text-gray-500 text-sm mt-2">Choose a folder from the left panel to view its files</p>
                </div>
              ) : loading ? (
                <div className="flex items-center justify-center h-64">
                  <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
                  <span className="ml-2 text-gray-600">Loading files...</span>
                </div>
              ) : filteredRecords.length === 0 ? (
                <div className="text-center py-12 bg-white rounded-lg border-2 border-dashed border-gray-300">
                  <Upload className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600 text-lg">
                    {filterMode === 'files' ? 'No files found' : filterMode === 'folders' ? 'No folders found' : 'No files or folders found'}
                  </p>
                  <p className="text-gray-500 text-sm mt-2">
                    {filterMode === 'files' ? 'Upload some files to get started' : filterMode === 'folders' ? 'Create a folder to get started' : 'Upload files or create folders to get started'}
                  </p>
                </div>
              ) : (
                <>
                  {viewMode === 'grid' ? (
                    // Grid View
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                      {getFilesWithVersionCount().map((item) => {
                        const isFolder = item.isFolder || item.type === 'folder';
                        const filename = item.original_name || item.name;
                        const displayName = item.original_name || item.name;
                        const isImage = !isFolder && isImageFile(filename);
                        
                        return (
                          <div
                            key={item.key}
                            className={`relative group border border-gray-200 bg-white rounded-lg p-3 hover:border-blue-300 hover:shadow-md transition-all ${!isFolder ? 'cursor-move' : 'cursor-pointer'}`}
                            title={displayName}
                            draggable={!isFolder}
                            onDragStart={(e) => {
                              if (!isFolder) {
                                e.dataTransfer.setData('application/json', JSON.stringify(item));
                                e.dataTransfer.effectAllowed = 'move';
                              }
                            }}
                            onDragOver={(e) => {
                              if (isFolder) {
                                e.preventDefault();
                                e.stopPropagation();
                                e.currentTarget.classList.add('ring-2', 'ring-blue-400', 'bg-blue-50');
                              }
                            }}
                            onDragLeave={(e) => {
                              if (isFolder) {
                                e.currentTarget.classList.remove('ring-2', 'ring-blue-400', 'bg-blue-50');
                              }
                            }}
                            onDrop={async (e) => {
                              if (isFolder) {
                                e.preventDefault();
                                e.stopPropagation();
                                e.currentTarget.classList.remove('ring-2', 'ring-blue-400', 'bg-blue-50');
                                
                                const fileData = e.dataTransfer.getData('application/json');
                                if (fileData) {
                                  try {
                                    const draggedFile = JSON.parse(fileData);
                                    console.log('Dropped file:', draggedFile);
                                    console.log('Destination folder:', item.key);
                                    // Move file to this folder - pass file directly
                                    await handleMoveFile(draggedFile, item.key);
                                  } catch (error) {
                                    console.error('Error parsing dragged file:', error);
                                    showError('Failed to move file. Please try again.');
                                  }
                                }
                              }
                            }}
                            onClick={() => {
                              if (isFolder) {
                                navigateIntoFolder(filename);
                              }
                            }}
                          >
                            {/* 3-dot menu button - for both files and folders */}
                            <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setContextMenu({
                                    show: true,
                                    x: e.clientX,
                                    y: e.clientY,
                                    file: item
                                  });
                                }}
                                className="bg-white shadow-md rounded-full p-1 hover:bg-gray-100 transition-colors"
                              >
                                <MoreVertical className="h-4 w-4 text-gray-600" />
                              </button>
                            </div>
                            
                            {/* File/Folder preview */}
                            <div className="aspect-square bg-gray-100 rounded-lg mb-2 overflow-hidden flex items-center justify-center">
                              {isFolder ? (
                                <Folder className="h-12 w-12 text-theme-blue" />
                              ) : (
                                <div>
                                  {getFileIcon(filename, 'large')}
                                </div>
                              )}
                            </div>
                            
                            {/* File/Folder info */}
                            <div className="text-xs space-y-1">
                              <p className="font-medium text-gray-800 truncate text-center">
                                {isFolder ? displayName : (displayName.substring(0, displayName.lastIndexOf('.')) || displayName)}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    // List View
                    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Name
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Type
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Modified
                            </th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {getFilesWithVersionCount().map((item) => {
                            const isFolder = item.isFolder || item.type === 'folder';
                            const filename = item.original_name || item.name;
                            const displayName = item.original_name || item.name;
                            const isImage = !isFolder && isImageFile(filename);
                            const fileExtension = isFolder ? 'Folder' : filename.split('.').pop().toUpperCase();
                            
                            return (
                              <tr 
                                key={item.key} 
                                className={`hover:bg-gray-50 ${!isFolder ? 'cursor-move' : 'cursor-pointer'}`}
                                draggable={!isFolder}
                                onDragStart={(e) => {
                                  if (!isFolder) {
                                    e.dataTransfer.setData('application/json', JSON.stringify(item));
                                    e.dataTransfer.effectAllowed = 'move';
                                  }
                                }}
                                onDragOver={(e) => {
                                  if (isFolder) {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    e.currentTarget.classList.add('bg-blue-50');
                                  }
                                }}
                                onDragLeave={(e) => {
                                  if (isFolder) {
                                    e.currentTarget.classList.remove('bg-blue-50');
                                  }
                                }}
                                onDrop={async (e) => {
                                  if (isFolder) {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    e.currentTarget.classList.remove('bg-blue-50');
                                    
                                    const fileData = e.dataTransfer.getData('application/json');
                                    if (fileData) {
                                      try {
                                        const draggedFile = JSON.parse(fileData);
                                        console.log('Dropped file:', draggedFile);
                                        console.log('Destination folder:', item.key);
                                        // Move file to this folder - pass file directly
                                        await handleMoveFile(draggedFile, item.key);
                                      } catch (error) {
                                        console.error('Error parsing dragged file:', error);
                                        showError('Failed to move file. Please try again.');
                                      }
                                    }
                                  }
                                }}
                                onClick={() => {
                                  if (isFolder) {
                                    navigateIntoFolder(filename);
                                  }
                                }}
                              >
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="flex items-center">
                                    {/* Removed older version indicator */}
                                    <div className="text-2xl mr-3">
                                      {isFolder ? <Folder className="h-6 w-6 text-theme-blue" /> : getFileIcon(filename)}
                                    </div>
                                    <div className="flex items-center space-x-2">
                                      <div className="text-sm font-medium text-gray-900 truncate max-w-md" title={displayName}>
                                        {isFolder ? displayName : (displayName.substring(0, displayName.lastIndexOf('.')) || displayName)}
                                      </div>
                                    </div>
                                  </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  {fileExtension}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  {item.last_modified ? new Date(item.last_modified).toLocaleDateString() : '-'}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setContextMenu({
                                        show: true,
                                        x: e.clientX,
                                        y: e.clientY,
                                        file: item
                                      });
                                    }}
                                    className="text-gray-600 hover:text-gray-900"
                                  >
                                    <MoreVertical className="h-4 w-4" />
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>

      {/* Context Menu */}
      {contextMenu.show && contextMenu.file && (
        <div
          className="fixed bg-white border border-gray-200 rounded-lg shadow-lg py-2 z-[70]"
          style={{ 
            left: `${contextMenu.x}px`, 
            top: `${contextMenu.y}px`,
            transform: 'translate(-50%, 0)'
          }}
        >
          {contextMenu.file.type === 'folder' ? (
            // Folder actions
            <>
              <button
                onClick={() => {
                  // Set up to create subfolder inside this folder
                  const currentPath = [...selectedPath, contextMenu.file.name].join('/');
                  setCreateSubfolderParent({
                    folderPath: currentPath
                  });
                  setShowCreateSubfolderModal(true);
                  setContextMenu({ show: false, x: 0, y: 0, file: null });
                }}
                className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex items-center"
              >
                <FolderPlus className="h-4 w-4 mr-2 text-theme-blue" />
                New Folder
              </button>
              <button
                onClick={() => {
                  setFolderToRename(contextMenu.file);
                  setRenameFolderValue(contextMenu.file.name);
                  setShowRenameFolderModal(true);
                  setContextMenu({ show: false, x: 0, y: 0, file: null });
                }}
                className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex items-center"
              >
                <Edit2 className="h-4 w-4 mr-2" />
                Rename
              </button>
              <button
                onClick={async () => {
                  // Use the same move functionality as files
                  setFileToMove(contextMenu.file);
                  setMoveDestinationPath([]);
                  setMoveDestinationKey('');
                  // Load all parent folders (Admin, Invoices, Procedures) as destination options
                  setAvailableFolders(parentFolders);
                  setShowMoveFileModal(true);
                  setContextMenu({ show: false, x: 0, y: 0, file: null });
                }}
                className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex items-center"
              >
                <Move className="h-4 w-4 mr-2 text-purple-600" />
                Move
              </button>
              <button
                onClick={() => {
                  setItemToDelete(contextMenu.file);
                  setShowDeleteConfirmModal(true);
                  setContextMenu({ show: false, x: 0, y: 0, file: null });
                }}
                className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 text-red-600 flex items-center"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </button>
            </>
          ) : (
            // File actions
            <>
              {contextMenu.file.hasMultipleVersions && (
                <button
                  onClick={() => {
                    const clientId = parentFolders.find(c => c.name === selectedPath[0])?.id;
                    if (clientId) {
                      fetchFileVersions(clientId, contextMenu.file.key, contextMenu.file.name);
                    }
                    setContextMenu({ show: false, x: 0, y: 0, file: null });
                  }}
                  className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex items-center border-b border-gray-200"
                >
                  <RefreshCw className="h-4 w-4 mr-2 text-theme-blue" />
                  <span className="flex-1">Versions</span>
                  <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full">
                    {contextMenu.file.versionCount}
                  </span>
                </button>
              )}
              <button
                onClick={() => {
                  setFileToRename(contextMenu.file);
                  // Extract filename without extension
                  const fullName = contextMenu.file.original_name || contextMenu.file.name;
                  const nameWithoutExt = fullName.includes('.') ? fullName.substring(0, fullName.lastIndexOf('.')) : fullName;
                  setRenameFileValue(nameWithoutExt);
                  setShowRenameFileModal(true);
                  setContextMenu({ show: false, x: 0, y: 0, file: null });
                }}
                className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex items-center"
              >
                <File className="h-4 w-4 mr-2" />
                Rename
              </button>
              <button
                onClick={async () => {
                  setFileToMove(contextMenu.file);
                  setMoveDestinationPath([]);
                  // Load all parent folders
                  setAvailableFolders(parentFolders);
                  setShowMoveFileModal(true);
                  setContextMenu({ show: false, x: 0, y: 0, file: null });
                }}
                className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex items-center"
              >
                <Move className="h-4 w-4 mr-2" />
                Move
              </button>
              {isPreviewable(contextMenu.file.name) && (
                <button
                  onClick={() => {
                    openPreview(contextMenu.file);
                    setContextMenu({ show: false, x: 0, y: 0, file: null });
                  }}
                  className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex items-center"
                >
                  <Eye className="h-4 w-4 mr-2" />
                  Preview
                </button>
              )}
              <button
                onClick={() => {
                  copyUrlToClipboard(contextMenu.file.url);
                  setContextMenu({ show: false, x: 0, y: 0, file: null });
                }}
                className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex items-center"
              >
                <Copy className="h-4 w-4 mr-2" />
                Copy URL
              </button>
              <button
                onClick={() => {
                  window.open(contextMenu.file.url, '_blank');
                  setContextMenu({ show: false, x: 0, y: 0, file: null });
                }}
                className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex items-center"
              >
                <Download className="h-4 w-4 mr-2" />
                Download
              </button>
              <button
                onClick={() => {
                  setItemToDelete(contextMenu.file);
                  setShowDeleteConfirmModal(true);
                  setContextMenu({ show: false, x: 0, y: 0, file: null });
                }}
                className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 text-red-600 flex items-center"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </button>
            </>
          )}
        </div>
      )}

      {/* Preview Modal */}
      {showPreviewModal && previewData.type === 'pdf' && (
        <PDFViewer
          fileUrl={previewData.url}
          fileName={previewData.name}
          onClose={() => setShowPreviewModal(false)}
          token={token}
        />
      )}

      {showPreviewModal && previewData.type === 'office' && (
        <OfficeViewer
          fileUrl={previewData.url}
          fileName={previewData.name}
          onClose={() => setShowPreviewModal(false)}
          token={token}
        />
      )}

      {showPreviewModal && previewData.type === 'image' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl h-[95vh] flex flex-col">
            {/* Header */}
            <div className="flex justify-between items-center px-3 py-2 bg-gray-50">
              <div className="flex items-center space-x-3">
                <h3 className="text-sm font-semibold text-gray-900 truncate max-w-md">{previewData.name}</h3>
              </div>
              
              {/* Controls */}
              <div className="flex items-center space-x-1">
                <button
                  onClick={() => window.open(previewData.url, '_blank')}
                  className="p-1.5 rounded-md hover:bg-gray-200 transition-colors"
                  title="Download Image"
                >
                  <Download className="h-4 w-4" />
                </button>
                
                <button
                  onClick={() => setShowPreviewModal(false)}
                  className="p-1.5 rounded-md hover:bg-gray-200 transition-colors"
                  title="Close"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
            
            {/* Image Content */}
            <div className="flex-1 overflow-auto bg-gray-100 flex items-center justify-center p-4">
              <img 
                src={previewData.url} 
                alt={previewData.name}
                className="max-w-full max-h-full object-contain"
              />
            </div>

            {/* Footer */}
            <div className="flex justify-between items-center px-3 py-2 border-t border-gray-200 bg-gray-50">
              <div className="text-xs text-gray-600">
                {'\u00A0'}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Customer Modal */}
      {showAddCustomerModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full max-h-[90vh] overflow-hidden shadow-2xl">
            {/* Sticky Header */}
            <div className="sticky top-0 z-10 px-6 py-4" style={{ background: 'linear-gradient(to right, var(--theme-primary), var(--theme-secondary))' }}>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="bg-white bg-opacity-20 rounded-full p-2">
                    <Users className="h-5 w-5 text-white" />
                  </div>
                  <h3 className="text-lg font-semibold text-white">
                    Add New Customer
                  </h3>
                </div>
                <button
                  onClick={resetCustomerForm}
                  className="text-white hover:text-blue-100 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-full p-2 transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Scrollable Content */}
            <div className="px-6 py-4 max-h-[calc(90vh-140px)] overflow-y-auto">
              <form onSubmit={handleAddCustomer} className="space-y-4">
                {/* First Name and Last Name */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      First Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={customerFormData.first_name}
                      onChange={(e) => setCustomerFormData({ ...customerFormData, first_name: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none transition-colors focus:border-theme-blue focus:ring-2 focus:ring-blue-200"
                      placeholder="Enter first name"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Last Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={customerFormData.last_name}
                      onChange={(e) => setCustomerFormData({ ...customerFormData, last_name: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none transition-colors focus:border-theme-blue focus:ring-2 focus:ring-blue-200"
                      placeholder="Enter last name"
                    />
                  </div>
                </div>

                {/* Pet Names */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-semibold text-gray-700">
                      Pet Names
                    </label>
                    <button
                      type="button"
                      onClick={addPet}
                      className="text-sm text-theme-blue hover:text-blue-800 flex items-center"
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Add Pet
                    </button>
                  </div>
                  
                  <div className="space-y-2">
                    {customerFormData.pets.map((pet, index) => (
                      <div key={index} className="flex items-center space-x-2">
                        <input
                          type="text"
                          value={pet.name}
                          onChange={(e) => updatePetName(index, e.target.value)}
                          className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none transition-colors focus:border-theme-blue focus:ring-2 focus:ring-blue-200"
                          placeholder="Enter pet name"
                        />
                        {customerFormData.pets.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removePet(index)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <X className="h-5 w-5" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Phone */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    value={customerFormData.phone}
                    onChange={(e) => setCustomerFormData({ ...customerFormData, phone: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none transition-colors focus:border-theme-blue focus:ring-2 focus:ring-blue-200"
                    placeholder="(555) 123-4567"
                  />
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={customerFormData.email}
                    onChange={(e) => setCustomerFormData({ ...customerFormData, email: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none transition-colors focus:border-theme-blue focus:ring-2 focus:ring-blue-200"
                    placeholder="customer@example.com"
                  />
                </div>

                {/* SMS Opt-in */}
                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    id="sms_opt_in"
                    checked={customerFormData.sms_opt_in}
                    onChange={(e) => setCustomerFormData({ ...customerFormData, sms_opt_in: e.target.checked })}
                    className="w-5 h-5 text-theme-blue border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                  />
                  <label htmlFor="sms_opt_in" className="text-sm text-gray-700">
                    SMS Opt-in
                  </label>
                </div>

                {/* Email Subscribed */}
                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    id="email_subscribed"
                    checked={customerFormData.email_subscribed}
                    onChange={(e) => setCustomerFormData({ ...customerFormData, email_subscribed: e.target.checked })}
                    className="w-5 h-5 text-theme-blue border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                  />
                  <label htmlFor="email_subscribed" className="text-sm text-gray-700">
                    Email Subscribed
                  </label>
                </div>
              </form>
            </div>

            {/* Sticky Footer */}
            <div className="sticky bottom-0 px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={resetCustomerForm}
                type="button"
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAddCustomer}
                disabled={loading}
                className="px-4 py-2 text-white rounded-lg transition-colors disabled:opacity-50"
                style={{ background: 'linear-gradient(to right, var(--theme-primary), var(--theme-secondary))' }}
              >
                {loading ? 'Adding...' : 'Add Customer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Folder Modal */}
      {showCreateFolderModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full shadow-2xl">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="bg-green-100 rounded-full p-2">
                    <FolderPlus className="h-5 w-5 text-green-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    New Folder
                  </h3>
                </div>
                <button
                  onClick={() => {
                    setShowCreateFolderModal(false);
                    setNewFolderName('');
                  }}
                  className="text-gray-400 hover:text-gray-600 rounded-full p-2 hover:bg-gray-100 transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="px-6 py-4">
              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-2">
                  Creating folder in: <span className="font-medium">{getBreadcrumb()}</span>
                </p>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Folder Name
                </label>
                <input
                  type="text"
                  value={newFolderName}
                  onChange={(e) => setNewFolderName(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && newFolderName.trim()) {
                      handleCreateFolder();
                    }
                  }}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-200"
                  placeholder="Enter folder name"
                  autoFocus
                />
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowCreateFolderModal(false);
                  setNewFolderName('');
                }}
                type="button"
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateFolder}
                disabled={loading || !newFolderName.trim()}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
              >
                {loading ? 'Creating...' : 'Create Folder'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Top Folder Modal */}
      {showCreateTopFolderModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full shadow-2xl">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="bg-green-100 rounded-full p-2">
                    <FolderPlus className="h-5 w-5 text-green-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Create Top-Level Folder
                  </h3>
                </div>
                <button
                  onClick={() => {
                    setShowCreateTopFolderModal(false);
                    setNewFolderName('');
                  }}
                  className="text-gray-400 hover:text-gray-600 rounded-full p-2 hover:bg-gray-100 transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="px-6 py-4">
              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-2">
                  Creating a new root-level folder in Hospital Records
                </p>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Folder Name
                </label>
                <input
                  type="text"
                  value={newFolderName}
                  onChange={(e) => setNewFolderName(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && newFolderName.trim()) {
                      handleCreateTopFolder();
                    }
                  }}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-200"
                  placeholder="Enter folder name"
                  autoFocus
                />
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowCreateTopFolderModal(false);
                  setNewFolderName('');
                }}
                type="button"
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateTopFolder}
                disabled={loading || !newFolderName.trim()}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
              >
                {loading ? 'Creating...' : 'Create Folder'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirmModal && itemToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full shadow-2xl">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <div className="bg-red-100 rounded-full p-2">
                  <Trash2 className="h-5 w-5 text-red-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Delete {itemToDelete.type === 'folder' ? 'Folder' : 'File'}
                </h3>
              </div>
            </div>

            {/* Content */}
            <div className="px-6 py-4">
              <p className="text-sm text-gray-700">
                Are you sure you want to delete <span className="font-semibold">{itemToDelete.name}</span>?
                {itemToDelete.type === 'folder' && (
                  <span className="block mt-2 text-red-600">
                    Warning: This will delete the folder and all its contents.
                  </span>
                )}
                <span className="block mt-2 text-gray-600">
                  This action cannot be undone.
                </span>
              </p>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowDeleteConfirmModal(false);
                  setItemToDelete(null);
                }}
                type="button"
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirm}
                disabled={loading}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center space-x-2"
              >
                <Trash2 className="h-4 w-4" />
                <span>{loading ? 'Deleting...' : 'Delete'}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rename Folder Modal */}
      {showRenameFolderModal && folderToRename && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full shadow-2xl">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="bg-blue-100 rounded-full p-2">
                    <File className="h-5 w-5 text-theme-blue" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Rename Folder
                  </h3>
                </div>
                <button
                  onClick={() => {
                    setShowRenameFolderModal(false);
                    setFolderToRename(null);
                    setRenameFolderValue('');
                  }}
                  className="text-gray-400 hover:text-gray-600 rounded-full p-2 hover:bg-gray-100 transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="px-6 py-4">
              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-2">
                  Current name: <span className="font-medium">{folderToRename.name}</span>
                </p>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  New Folder Name
                </label>
                <input
                  type="text"
                  value={renameFolderValue}
                  onChange={(e) => setRenameFolderValue(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && renameFolderValue.trim()) {
                      handleRenameFolder();
                    }
                  }}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-theme-blue focus:ring-2 focus:ring-blue-200"
                  placeholder="Enter new folder name"
                  autoFocus
                />
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowRenameFolderModal(false);
                  setFolderToRename(null);
                  setRenameFolderValue('');
                }}
                type="button"
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleRenameFolder}
                disabled={loading || !renameFolderValue.trim()}
                className="px-4 py-2 bg-theme-blue text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {loading ? 'Renaming...' : 'Rename'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rename File Modal */}
      {showRenameFileModal && fileToRename && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full shadow-2xl">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="bg-green-100 rounded-full p-2">
                    <File className="h-5 w-5 text-green-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Rename File
                  </h3>
                </div>
                <button
                  onClick={() => {
                    setShowRenameFileModal(false);
                    setFileToRename(null);
                    setRenameFileValue('');
                  }}
                  className="text-gray-400 hover:text-gray-600 rounded-full p-2 hover:bg-gray-100 transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="px-6 py-4">
              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-2">
                  Current name: <span className="font-medium">{fileToRename.original_name || fileToRename.name}</span>
                </p>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  New File Name
                </label>
                <div className="flex items-center space-x-2">
                  <input
                    type="text"
                    value={renameFileValue}
                    onChange={(e) => setRenameFileValue(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && renameFileValue.trim()) {
                        handleRenameFile();
                      }
                    }}
                    className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-200"
                    placeholder="Enter file name"
                    autoFocus
                  />
                  {fileToRename && (fileToRename.original_name || fileToRename.name).includes('.') && (
                    <span className="text-sm font-medium text-gray-600 bg-gray-100 px-3 py-3 rounded-lg border border-gray-300">
                      {(fileToRename.original_name || fileToRename.name).substring((fileToRename.original_name || fileToRename.name).lastIndexOf('.'))}
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  File extension is preserved automatically
                </p>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowRenameFileModal(false);
                  setFileToRename(null);
                  setRenameFileValue('');
                }}
                type="button"
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleRenameFile}
                disabled={loading || !renameFileValue.trim()}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
              >
                {loading ? 'Renaming...' : 'Rename'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Recycle Bin Modal */}
      {showRecycleBin && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden shadow-2xl">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="bg-gray-100 rounded-full p-2">
                    <Trash2 className="h-5 w-5 text-gray-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      Recycle Bin - {selectedPath[0]}
                    </h3>
                    {recycleBinBrowseFolder && (
                      <p className="text-sm text-gray-500">
                        Viewing: {recycleBinBrowseFolder}
                      </p>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowRecycleBin(false);
                    setRecycleBinBrowseFolder(null);
                  }}
                  className="text-gray-400 hover:text-gray-600 rounded-full p-2 hover:bg-gray-100 transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              {/* Back button when browsing folder */}
              {recycleBinBrowseFolder && (
                <div className="mt-3">
                  <button
                    onClick={() => {
                      setRecycleBinBrowseFolder(null);
                      fetchRecycleBin(selectedPath[0]);
                    }}
                    className="text-sm text-theme-blue hover:text-blue-700 flex items-center space-x-1"
                  >
                    <ChevronDown className="h-4 w-4 transform rotate-90" />
                    <span>Back to Recycle Bin</span>
                  </button>
                </div>
              )}
            </div>

            {/* Content */}
            <div className="px-6 py-4 max-h-[calc(90vh-140px)] overflow-y-auto">
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw className="h-6 w-6 animate-spin text-gray-400" />
                  <span className="ml-2 text-gray-600">Loading recycle bin...</span>
                </div>
              ) : recycleBinItems.length === 0 ? (
                <div className="text-center py-12">
                  <Trash2 className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600 text-lg">Recycle bin is empty</p>
                  <p className="text-gray-500 text-sm mt-2">Deleted items will appear here</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {recycleBinItems.map((item) => (
                    <div
                      key={item.key || item.name}
                      className={`flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 ${
                        item.type === 'folder' && !recycleBinBrowseFolder ? 'cursor-pointer' : ''
                      }`}
                      onClick={() => {
                        // Only allow clicking into folders from main recycle bin view
                        if (item.type === 'folder' && !recycleBinBrowseFolder) {
                          browseDeletedFolder(selectedPath[0], item.name);
                        }
                      }}
                    >
                      <div className="flex items-center space-x-3">
                        <div className="text-2xl">
                          {item.type === 'folder' ? (
                            <Folder className="h-6 w-6 text-theme-blue" />
                          ) : (
                            getFileIcon(item.name)
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">
                            {item.name}
                            {item.item_count > 0 && (
                              <span className="ml-2 text-xs text-gray-500">
                                ({item.item_count} items)
                              </span>
                            )}
                          </p>
                          <p className="text-sm text-gray-500">
                            Deleted: {item.deleted_at ? new Date(item.deleted_at).toLocaleDateString() : 'Unknown'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        {/* Only show restore for files OR top-level folders (not subfolders) */}
                        {(item.type !== 'folder' || !recycleBinBrowseFolder) && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRestoreItem(item);
                            }}
                            disabled={loading}
                            className="px-3 py-1.5 text-sm bg-green-600 text-white rounded hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center space-x-1"
                          >
                            <RotateCcw className="h-3.5 w-3.5" />
                            <span>Restore</span>
                          </button>
                        )}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handlePermanentDelete(item);
                          }}
                          disabled={loading}
                          className="px-3 py-1.5 text-sm bg-red-600 text-white rounded hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center space-x-1"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                          <span>Delete Forever</span>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end">
              <button
                onClick={() => {
                  setShowRecycleBin(false);
                  setRecycleBinBrowseFolder(null);
                }}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Global Recycle Bin Modal */}
      {showGlobalRecycleBin && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-5xl w-full max-h-[90vh] overflow-hidden shadow-2xl">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="bg-gray-100 rounded-full p-2">
                    <Trash2 className="h-5 w-5 text-gray-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      Recycle Bin - Hospital Records
                    </h3>
                    {globalRecycleBinBrowseFolder ? (
                      <p className="text-sm text-gray-600">
                        Viewing: {globalRecycleBinBrowseFolder} (from {globalRecycleBinBrowseParent})
                      </p>
                    ) : (
                      <p className="text-sm text-gray-600">
                        {globalRecycleBinItems.length} item{globalRecycleBinItems.length !== 1 ? 's' : ''} in recycle bin
                      </p>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowGlobalRecycleBin(false);
                    setGlobalRecycleBinBrowseFolder(null);
                    setGlobalRecycleBinBrowseParent(null);
                    setGlobalRecycleBinBrowsePath([]);
                  }}
                  className="text-gray-400 hover:text-gray-600 rounded-full p-2 hover:bg-gray-100 transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              {/* Back button and breadcrumb when browsing folder */}
              {globalRecycleBinBrowseFolder && (
                <div className="mt-3 space-y-2">
                  <button
                    onClick={() => {
                      if (globalRecycleBinBrowsePath.length > 0) {
                        // Go back one level
                        const newPath = globalRecycleBinBrowsePath.slice(0, -1);
                        setGlobalRecycleBinBrowsePath(newPath);
                        browseGlobalDeletedFolder(
                          globalRecycleBinBrowseParent,
                          globalRecycleBinBrowseFolder,
                          newPath.join('/')
                        );
                      } else {
                        // Go back to main recycle bin
                        setGlobalRecycleBinBrowseFolder(null);
                        setGlobalRecycleBinBrowseParent(null);
                        setGlobalRecycleBinBrowsePath([]);
                        fetchGlobalRecycleBin();
                      }
                    }}
                    className="text-sm text-theme-blue hover:text-blue-700 flex items-center space-x-1"
                  >
                    <ChevronDown className="h-4 w-4 transform rotate-90" />
                    <span>Back {globalRecycleBinBrowsePath.length > 0 ? 'to Parent Folder' : 'to Recycle Bin'}</span>
                  </button>
                  {globalRecycleBinBrowsePath.length > 0 && (
                    <div className="text-xs text-gray-500 pl-6">
                      Path: {globalRecycleBinBrowseFolder} / {globalRecycleBinBrowsePath.join(' / ')}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Content */}
            <div className="px-6 py-4 max-h-[calc(90vh-140px)] overflow-y-auto">
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw className="h-6 w-6 animate-spin text-gray-400" />
                  <span className="ml-2 text-gray-600">Loading recycle bin...</span>
                </div>
              ) : globalRecycleBinItems.length === 0 ? (
                <div className="text-center py-12">
                  <Trash2 className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600 text-lg">Recycle bin is empty</p>
                  <p className="text-gray-500 text-sm mt-2">Deleted items from all folders will appear here</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {globalRecycleBinItems.map((item) => (
                    <div
                      key={item.key || item.name}
                      className={`flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 ${
                        item.type === 'folder' ? 'cursor-pointer' : ''
                      }`}
                      onClick={() => {
                        if (item.type === 'folder') {
                          if (!globalRecycleBinBrowseFolder && item.folder_name) {
                            // Main view - browse into deleted folder
                            browseGlobalDeletedFolder(item.folder_name, item.name);
                          } else if (globalRecycleBinBrowseFolder) {
                            // Browsing view - go deeper into subfolder
                            const newPath = [...globalRecycleBinBrowsePath, item.name];
                            setGlobalRecycleBinBrowsePath(newPath);
                            browseGlobalDeletedFolder(
                              globalRecycleBinBrowseParent,
                              globalRecycleBinBrowseFolder,
                              newPath.join('/')
                            );
                          }
                        }
                      }}
                    >
                      <div className="flex items-center space-x-3 flex-1">
                        <div>
                          {item.type === 'folder' ? (
                            <Folder className="h-6 w-6 text-theme-blue" />
                          ) : (
                            getFileIcon(item.name)
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 truncate">
                            {item.name}
                            {item.item_count > 0 && (
                              <span className="ml-2 text-xs text-gray-500">
                                ({item.item_count} items)
                              </span>
                            )}
                          </p>
                          <div className="flex items-center space-x-4 text-xs text-gray-500 mt-1">
                            {item.folder_name && (
                              <span className="flex items-center space-x-1">
                                <Folder className="h-3 w-3" />
                                <span>{item.folder_name}</span>
                              </span>
                            )}
                            <span>
                              Deleted: {item.deleted_at ? new Date(item.deleted_at).toLocaleDateString() : 'Unknown'}
                            </span>
                            {item.deleted_by && (
                              <span className="text-gray-400">
                                by {item.deleted_by}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2 ml-4">
                        {/* Only show restore for files OR top-level folders (not subfolders) */}
                        {(item.type !== 'folder' || !globalRecycleBinBrowseFolder) && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRestoreGlobalItem(item);
                            }}
                            disabled={loading}
                            className="px-3 py-1.5 text-sm bg-green-600 text-white rounded hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center space-x-1"
                          >
                            <RotateCcw className="h-3.5 w-3.5" />
                            <span>Restore</span>
                          </button>
                        )}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handlePermanentDeleteGlobal(item);
                          }}
                          disabled={loading}
                          className="px-3 py-1.5 text-sm bg-red-600 text-white rounded hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center space-x-1"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                          <span>Delete Forever</span>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-between items-center">
              <p className="text-sm text-gray-600">
                Items are automatically deleted after the configured retention period
              </p>
              <button
                onClick={() => setShowGlobalRecycleBin(false)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Versions Modal */}
      {showVersionsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-3xl w-full max-h-[80vh] overflow-hidden shadow-2xl">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="bg-blue-100 rounded-full p-2">
                    <RefreshCw className="h-5 w-5 text-theme-blue" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      Version History
                    </h3>
                    <p className="text-sm text-gray-600">{selectedFileName}</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowVersionsModal(false)}
                  className="text-gray-400 hover:text-gray-600 rounded-full p-2 hover:bg-gray-100 transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="px-6 py-4 max-h-[calc(80vh-140px)] overflow-y-auto">
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw className="h-6 w-6 animate-spin text-gray-400" />
                  <span className="ml-2 text-gray-600">Loading versions...</span>
                </div>
              ) : selectedFileVersions.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-gray-600">No versions found</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {selectedFileVersions.map((version, index) => (
                    <div
                      key={version.key}
                      className={`flex items-center justify-between p-4 border rounded-lg ${
                        index === 0 ? 'border-theme-blue bg-blue-50' : 'border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center space-x-4 flex-1">
                        <div>
                          <div className="flex items-center space-x-2">
                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold ${
                              index === 0 ? 'bg-theme-blue text-white' : 'bg-gray-200 text-gray-700'
                            }`}>
                              Version {version.version}
                            </span>
                            {index === 0 && (
                              <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full font-medium">
                                Current
                              </span>
                            )}
                          </div>
                          <div className="mt-2 text-sm text-gray-600">
                            <div>Uploaded: {new Date(version.uploaded_at).toLocaleString()}</div>
                            {version.uploaded_by && (
                              <div>By: {version.uploaded_by}</div>
                            )}
                            <div className="text-xs text-gray-500">
                              Size: {(version.size / 1024).toFixed(2)} KB
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => {
                            setPreviewData({
                              name: selectedFileName,
                              url: version.url,
                              type: getFileType(selectedFileName)
                            });
                            setShowPreviewModal(true);
                          }}
                          className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors flex items-center space-x-1"
                        >
                          <Eye className="h-3.5 w-3.5" />
                          <span>Preview</span>
                        </button>
                        <button
                          onClick={() => window.open(version.url, '_blank')}
                          className="px-3 py-1.5 text-sm bg-theme-blue text-white rounded hover:bg-blue-700 transition-colors flex items-center space-x-1"
                        >
                          <Download className="h-3.5 w-3.5" />
                          <span>Download</span>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end">
              <button
                onClick={() => setShowVersionsModal(false)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error Modal */}
      {showErrorModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full shadow-2xl">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <div className="bg-red-100 rounded-full p-2">
                  <X className="h-5 w-5 text-red-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Error
                </h3>
              </div>
            </div>

            {/* Content */}
            <div className="px-6 py-4">
              <p className="text-sm text-gray-700">
                {errorMessage}
              </p>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end">
              <button
                onClick={() => setShowErrorModal(false)}
                className="px-4 py-2 bg-theme-blue text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Move File Modal */}
      {showMoveFileModal && fileToMove && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full shadow-2xl max-h-[80vh] flex flex-col">
            {/* Header - Sticky */}
            <div className="px-6 py-4 border-b border-gray-200 flex-shrink-0">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="bg-blue-100 rounded-full p-2">
                    <Move className="h-5 w-5 text-theme-blue" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      Move File
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">
                      Moving: {fileToMove.original_name || fileToMove.name}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowMoveFileModal(false);
                    setFileToMove(null);
                    setMoveDestinationPath([]);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Content - Scrollable Folder Navigator */}
            <div className="flex-1 overflow-y-auto px-6 py-4">
              <p className="text-sm text-gray-600 mb-4">
                Select a destination folder:
              </p>
              
              {/* Breadcrumb showing current selection */}
              {moveDestinationPath.length > 0 && (
                <div className="mb-4 p-3 bg-blue-50 rounded-lg flex items-center justify-between">
                  <div className="flex items-center">
                    <Folder className="h-4 w-4 text-theme-blue mr-2" />
                    <span className="text-sm text-blue-800 font-medium">
                      {moveDestinationPath.join(' / ')}
                    </span>
                  </div>
                  <span className="text-xs text-theme-blue">Selected</span>
                </div>
              )}

              {/* Folder List */}
              <div className="space-y-2">
                {availableFolders.map((folder) => (
                  <button
                    key={folder.key || folder.name}
                    onClick={async () => {
                      // Store the selected folder key
                      setMoveDestinationKey(folder.key || `Hospital Records/${folder.name}/`);
                      
                      // If this is a parent folder, load its subfolders
                      if (!moveDestinationPath.length) {
                        const subfolders = await loadAvailableFolders(folder.name);
                        setMoveDestinationPath([folder.name]);
                        setAvailableFolders(subfolders);
                      } else {
                        // Navigate deeper
                        const newPath = [...moveDestinationPath, folder.name];
                        setMoveDestinationPath(newPath);
                        const rootFolder = newPath[0];
                        const subfolders = await loadAvailableFolders(rootFolder, newPath.slice(1));
                        setAvailableFolders(subfolders);
                      }
                    }}
                    className="w-full flex items-center space-x-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-blue-300 transition-all text-left"
                  >
                    <Folder className="h-5 w-5 text-theme-blue flex-shrink-0" />
                    <span className="text-sm font-medium text-gray-900 truncate flex-1">
                      {folder.name}
                    </span>
                    <ChevronRight className="h-4 w-4 text-gray-400 flex-shrink-0" />
                  </button>
                ))}
                
                {availableFolders.length === 0 && moveDestinationPath.length > 0 && (
                  <div className="text-center text-gray-500 text-sm py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                    <Folder className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                    <p>No subfolders in this folder</p>
                    <p className="text-xs mt-1">Click "Move Here" to move the file to this location</p>
                  </div>
                )}
                
                {availableFolders.length === 0 && moveDestinationPath.length === 0 && (
                  <p className="text-center text-gray-500 text-sm py-8">
                    No folders available
                  </p>
                )}
              </div>
            </div>

            {/* Footer - Sticky */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-between flex-shrink-0">
              <button
                onClick={() => {
                  if (moveDestinationPath.length > 1) {
                    // Go back one level
                    const newPath = moveDestinationPath.slice(0, -1);
                    setMoveDestinationPath(newPath);
                    const rootFolder = newPath[0];
                    loadAvailableFolders(rootFolder, newPath.slice(1)).then(setAvailableFolders);
                  } else if (moveDestinationPath.length === 1) {
                    // Go back to root folders
                    setMoveDestinationPath([]);
                    setAvailableFolders(parentFolders);
                  }
                }}
                disabled={moveDestinationPath.length === 0}
                className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                ← Back
              </button>
              <div className="flex space-x-3">
                <button
                  onClick={() => {
                    setShowMoveFileModal(false);
                    setFileToMove(null);
                    setMoveDestinationPath([]);
                    setMoveDestinationKey('');
                  }}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    if (moveDestinationKey) {
                      handleMoveFile(fileToMove, moveDestinationKey);
                    }
                  }}
                  disabled={!moveDestinationKey}
                  className="px-4 py-2 bg-theme-blue text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Move Here
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Forever Confirmation Modal */}
      {showDeleteForeverModal && itemToDeleteForever && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full shadow-2xl">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <div className="bg-red-100 rounded-full p-2">
                  <Trash2 className="h-5 w-5 text-red-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Permanently Delete File
                </h3>
              </div>
            </div>

            {/* Content */}
            <div className="px-6 py-4">
              <p className="text-sm text-gray-700 mb-2">
                Are you sure you want to <span className="font-semibold text-red-600">permanently delete</span> this file?
              </p>
              <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {itemToDeleteForever.original_name || itemToDeleteForever.name}
                </p>
              </div>
              <p className="text-xs text-red-600 mt-3">
                ⚠️ This action cannot be undone. The file will be permanently removed.
              </p>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowDeleteForeverModal(false);
                  setItemToDeleteForever(null);
                }}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (itemToDeleteForever.isGlobal) {
                    confirmPermanentDeleteGlobal();
                  } else {
                    confirmPermanentDelete();
                  }
                }}
                disabled={loading}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {loading ? 'Deleting...' : 'Delete Forever'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Left Panel Context Menu */}
      {leftPanelContextMenu.show && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setLeftPanelContextMenu({ show: false, x: 0, y: 0, folder: null })}
          />
          <div
            className="fixed bg-white border border-gray-200 rounded-lg shadow-xl z-50 py-2 min-w-[180px]"
            style={{
              left: `${leftPanelContextMenu.x}px`,
              top: `${leftPanelContextMenu.y}px`,
            }}
          >
            {/* Create New Folder */}
            <button
              onClick={() => {
                setCreateSubfolderParent({
                  folderPath: leftPanelContextMenu.folder.name
                });
                setShowCreateSubfolderModal(true);
                setLeftPanelContextMenu({ show: false, x: 0, y: 0, folder: null });
              }}
              className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex items-center"
            >
              <FolderPlus className="h-4 w-4 mr-2 text-theme-blue" />
              Create New Folder
            </button>
            
            {/* Rename */}
            <button
              onClick={() => {
                setTopFolderToRename(leftPanelContextMenu.folder);
                setRenameTopFolderValue(leftPanelContextMenu.folder.name);
                setShowRenameTopFolderModal(true);
                setLeftPanelContextMenu({ show: false, x: 0, y: 0, folder: null });
              }}
              className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex items-center"
            >
              <Edit2 className="h-4 w-4 mr-2" />
              Rename
            </button>
            
            {/* Move */}
            <button
              onClick={() => {
                setTopFolderToMove(leftPanelContextMenu.folder);
                setMoveDestinationPath([]);
                setMoveDestinationKey('');
                // Load all other folders except the one being moved
                const otherFolders = parentFolders.filter(f => f.name !== leftPanelContextMenu.folder.name);
                setAvailableFolders(otherFolders);
                setShowMoveTopFolderModal(true);
                setLeftPanelContextMenu({ show: false, x: 0, y: 0, folder: null });
              }}
              className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex items-center"
            >
              <Move className="h-4 w-4 mr-2" />
              Move
            </button>
            
            {/* Delete */}
            <button
              onClick={() => {
                handleDeleteTopFolder(leftPanelContextMenu.folder);
                setLeftPanelContextMenu({ show: false, x: 0, y: 0, folder: null });
              }}
              className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex items-center text-red-600"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </button>
          </div>
        </>
      )}

      {/* Rename Top Folder Modal */}
      {showRenameTopFolderModal && topFolderToRename && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full shadow-2xl">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="bg-blue-100 rounded-full p-2">
                    <File className="h-5 w-5 text-theme-blue" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Rename Folder
                  </h3>
                </div>
                <button
                  onClick={() => {
                    setShowRenameTopFolderModal(false);
                    setTopFolderToRename(null);
                    setRenameTopFolderValue('');
                  }}
                  className="text-gray-400 hover:text-gray-600 rounded-full p-2 hover:bg-gray-100 transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="px-6 py-4">
              <div className="mb-4">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  New Folder Name
                </label>
                <input
                  type="text"
                  value={renameTopFolderValue}
                  onChange={(e) => setRenameTopFolderValue(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && renameTopFolderValue.trim()) {
                      handleRenameTopFolder();
                    }
                  }}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-theme-blue focus:ring-2 focus:ring-blue-200"
                  placeholder="Enter new folder name"
                  autoFocus
                />
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowRenameTopFolderModal(false);
                  setTopFolderToRename(null);
                  setRenameTopFolderValue('');
                }}
                type="button"
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleRenameTopFolder}
                disabled={loading || !renameTopFolderValue.trim()}
                className="px-4 py-2 bg-theme-blue text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {loading ? 'Renaming...' : 'Rename'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Move Top Folder Modal */}
      {showMoveTopFolderModal && topFolderToMove && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full shadow-2xl max-h-[80vh] flex flex-col">
            {/* Header - Sticky */}
            <div className="px-6 py-4 border-b border-gray-200 flex-shrink-0">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="bg-blue-100 rounded-full p-2">
                    <Move className="h-5 w-5 text-theme-blue" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      Move Folder
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">
                      Moving "{topFolderToMove.name}" into another folder
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowMoveTopFolderModal(false);
                    setTopFolderToMove(null);
                    setMoveDestinationPath([]);
                    setMoveDestinationKey('');
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Content - Scrollable */}
            <div className="flex-1 overflow-y-auto px-6 py-4">
              <p className="text-sm text-gray-600 mb-4">
                Select a destination folder:
              </p>
              
              {/* Selected destination display */}
              {moveDestinationKey && (
                <div className="mb-4 p-3 bg-blue-50 rounded-lg flex items-center justify-between">
                  <div className="flex items-center">
                    <Folder className="h-4 w-4 text-theme-blue mr-2" />
                    <span className="text-sm text-blue-800 font-medium">
                      Destination selected
                    </span>
                  </div>
                  <span className="text-xs text-theme-blue">Ready to move</span>
                </div>
              )}

              {/* Folder List */}
              <div className="space-y-2">
                {availableFolders.map((folder) => (
                  <button
                    key={folder.path || folder.key || folder.name}
                    onClick={() => {
                      // Store the selected folder path
                      setMoveDestinationKey(folder.path || folder.key || `Hospital Records/${folder.name}/`);
                    }}
                    className="w-full flex items-center space-x-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-blue-300 transition-all text-left"
                  >
                    <Folder className="h-5 w-5 text-theme-blue flex-shrink-0" />
                    <span className="text-sm font-medium text-gray-900 truncate flex-1">
                      {folder.name}
                    </span>
                  </button>
                ))}
                
                {availableFolders.length === 0 && (
                  <p className="text-center text-gray-500 text-sm py-8">
                    No other folders available
                  </p>
                )}
              </div>
            </div>

            {/* Footer - Sticky */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end space-x-3 flex-shrink-0">
              <button
                onClick={() => {
                  setShowMoveTopFolderModal(false);
                  setTopFolderToMove(null);
                  setMoveDestinationPath([]);
                  setMoveDestinationKey('');
                }}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (moveDestinationKey) {
                    handleMoveTopFolder(moveDestinationKey);
                  }
                }}
                disabled={!moveDestinationKey}
                className="px-4 py-2 bg-theme-blue text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Move Here
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Restore Confirmation Modal */}
      {showRestoreConfirmModal && itemToRestore && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full shadow-2xl">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <div className="bg-green-100 rounded-full p-2">
                  <RotateCcw className="h-5 w-5 text-green-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Restore {itemToRestore.type === 'folder' ? 'Folder' : 'File'}
                </h3>
              </div>
            </div>

            {/* Content */}
            <div className="px-6 py-4">
              <p className="text-gray-700 mb-4">
                Are you sure you want to restore this {itemToRestore.type === 'folder' ? 'folder' : 'file'}?
              </p>
              
              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <div className="flex items-center space-x-2">
                  {itemToRestore.type === 'folder' ? (
                    <Folder className="h-5 w-5 text-theme-blue flex-shrink-0" />
                  ) : (
                    <File className="h-5 w-5 text-gray-500 flex-shrink-0" />
                  )}
                  <p className="font-medium text-gray-900 break-all">
                    {itemToRestore.name}
                  </p>
                </div>
                
                {itemToRestore.item_count > 0 && (
                  <p className="text-sm text-gray-600 ml-7">
                    Contains {itemToRestore.item_count} {itemToRestore.item_count === 1 ? 'item' : 'items'}
                  </p>
                )}
                
                {itemToRestore.folder_name && (
                  <p className="text-sm text-gray-600 ml-7">
                    Folder: {itemToRestore.folder_name}
                  </p>
                )}
                
                {itemToRestore.deleted_at && (
                  <p className="text-xs text-gray-500 ml-7">
                    Deleted: {new Date(itemToRestore.deleted_at).toLocaleString()}
                  </p>
                )}
              </div>
              
              <p className="text-sm text-gray-600 mt-4">
                The {itemToRestore.type === 'folder' ? 'folder and all its contents will' : 'file will'} be restored to {itemToRestore.type === 'folder' ? 'its' : 'its'} original location.
              </p>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowRestoreConfirmModal(false);
                  setItemToRestore(null);
                }}
                disabled={loading}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmRestore}
                disabled={loading}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center space-x-2"
              >
                {loading ? (
                  <>
                    <RefreshCw className="h-4 w-4 animate-spin" />
                    <span>Restoring...</span>
                  </>
                ) : (
                  <>
                    <RotateCcw className="h-4 w-4" />
                    <span>Restore</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}


      {/* Tree Context Menu */}
      {treeContextMenu.show && (
        <>
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setTreeContextMenu({ show: false, x: 0, y: 0, folder: null, folderPath: '' })}
          />
          <div
            className="fixed bg-white rounded-lg shadow-xl border border-gray-200 py-1 z-50 min-w-[180px]"
            style={{
              left: `${treeContextMenu.x}px`,
              top: `${treeContextMenu.y}px`
            }}
          >
            {/* New Folder */}
            <button
              onClick={() => {
                setCreateSubfolderParent({
                  folderPath: treeContextMenu.folderPath
                });
                setShowCreateSubfolderModal(true);
                setTreeContextMenu({ show: false, x: 0, y: 0, folder: null, folderPath: '' });
              }}
              className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex items-center space-x-2"
            >
              <FolderPlus className="h-4 w-4 text-theme-blue" />
              <span>New Folder</span>
            </button>
            
            {/* Rename */}
            <button
              onClick={() => {
                setFolderToRename(treeContextMenu.folder);
                setRenameFolderValue(treeContextMenu.folder.name);
                setShowRenameFolderModal(true);
                setTreeContextMenu({ show: false, x: 0, y: 0, folder: null, folderPath: '' });
              }}
              className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex items-center space-x-2"
            >
              <Edit2 className="h-4 w-4 text-gray-600" />
              <span>Rename</span>
            </button>
            
            {/* Delete */}
            <button
              onClick={() => {
                setItemToDelete(treeContextMenu.folder);
                setShowDeleteConfirmModal(true);
                setTreeContextMenu({ show: false, x: 0, y: 0, folder: null, folderPath: '' });
              }}
              className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 text-red-600 flex items-center space-x-2"
            >
              <Trash2 className="h-4 w-4" />
              <span>Delete</span>
            </button>
          </div>
        </>
      )}

      {/* Create Subfolder Modal */}
      {showCreateSubfolderModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full shadow-2xl">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <div className="bg-blue-100 rounded-full p-2">
                  <FolderPlus className="h-5 w-5 text-theme-blue" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">
                  New Folder
                </h3>
              </div>
            </div>

            {/* Content */}
            <div className="px-6 py-4">
              <p className="text-sm text-gray-600 mb-4">
                Create a new subfolder inside: <span className="font-medium">{createSubfolderParent.folderPath}</span>
              </p>
              
              <input
                type="text"
                value={newSubfolderName}
                onChange={(e) => setNewSubfolderName(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleCreateSubfolder();
                  }
                }}
                placeholder="Folder name"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                autoFocus
              />
            </div>

            {/* Actions */}
            <div className="px-6 py-4 bg-gray-50 rounded-b-xl flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowCreateSubfolderModal(false);
                  setNewSubfolderName('');
                }}
                className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateSubfolder}
                disabled={!newSubfolderName.trim()}
                className="px-4 py-2 bg-theme-blue text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                <FolderPlus className="h-4 w-4" />
                <span>Create</span>
              </button>
            </div>
          </div>
        </div>
      )}


      {/* Notification Toast */}
      {notification.show && (
        <div className="fixed top-4 right-4 z-[9999] animate-slide-in-right">
          <div className={`flex items-center space-x-3 px-4 py-3 rounded-lg shadow-lg ${
            notification.type === 'success' 
              ? 'bg-green-50 border border-green-200' 
              : 'bg-red-50 border border-red-200'
          }`}>
            <div className={`flex-shrink-0 ${
              notification.type === 'success' ? 'text-green-600' : 'text-red-600'
            }`}>
              {notification.type === 'success' ? (
                <Check className="h-5 w-5" />
              ) : (
                <AlertCircle className="h-5 w-5" />
              )}
            </div>
            <p className={`text-sm font-medium ${
              notification.type === 'success' ? 'text-green-800' : 'text-red-800'
            }`}>
              {notification.message}
            </p>
            <button
              onClick={() => setNotification({ show: false, message: '', type: 'success' })}
              className={`flex-shrink-0 ml-2 ${
                notification.type === 'success' ? 'text-green-600 hover:text-green-800' : 'text-red-600 hover:text-red-800'
              }`}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default HospitalRecords;
